const GALLERY_DEBUG = (() => {
  try {
    const debugParam = new URLSearchParams(window.location.search).get('debug');
    if (debugParam === null) return false;
    return !['0', 'false', 'off'].includes(String(debugParam).toLowerCase());
  } catch {
    return false;
  }
})();
function gDebug(...args) {
  // Gallery console logs intentionally disabled.
  void args;
}
// Persistent gallery image cache (global)
const galleryImageCache = new Map(); // url -> true
const galleryImageInFlight = new Set(); // url -> true

function collectGalleryImageUrls(filterFn) {
  const urls = new Set();
  document.querySelectorAll('.flavour-option[data-product-images]').forEach(opt => {
    if (filterFn && !filterFn(opt)) return;
    let images = [];
    try {
      images = JSON.parse(opt.dataset.productImages || '[]');
    } catch {
      images = [];
    }
    images.forEach(src => {
      if (src) urls.add(src);
    });
  });
  return Array.from(urls);
}

function preloadGalleryUrls(urls) {
  if (!urls || !urls.length) return;

  const batchSize = 6;
  const delay = 120;

  urls.forEach((src, i) => {
    const batchIndex = Math.floor(i / batchSize);
    setTimeout(() => {
      if (galleryImageCache.has(src) || galleryImageInFlight.has(src)) return;
      galleryImageInFlight.add(src);
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = src;
      img.onload = () => {
        galleryImageInFlight.delete(src);
        galleryImageCache.set(src, true);
      };
      img.onerror = () => {
        galleryImageInFlight.delete(src);
      };
    }, batchIndex * delay);
  });
}

// Preload all gallery images to avoid delay on first switch
function preloadGalleryImages() {
  document.querySelectorAll('.flavour-option[data-product-images]').forEach(opt => {
    let images = [];
    try {
      images = JSON.parse(opt.dataset.productImages || '[]');
    } catch {
      images = [];
    }
    images.forEach(src => {
      if (!src || galleryImageCache.has(src)) return;
      const img = new Image();
      img.src = src;
      img.onload = () => galleryImageCache.set(src, true);
    });
  });
}

// PURCHASE TYPE STATE (must be defined at top so it's available everywhere)
const purchaseTypeState = {
  type: 'onetime'
};
const buySectionRoot = document.querySelector('.buy-section');
const petDepositEnabled = buySectionRoot?.dataset?.petDepositEnabled === 'true';
const petDepositTags = String(buySectionRoot?.dataset?.petDepositTags || '')
  .split(',')
  .map(tag => tag.trim())
  .filter(Boolean);
const petDepositLabelText = buySectionRoot?.dataset?.petDepositLabel || 'PET deposit';
const bottlePriceCents = parseInt(buySectionRoot?.dataset?.bottlePriceCents || '0', 10) || 0;
const hasBottlePrice = buySectionRoot?.dataset?.hasBottlePrice === 'true';
const buySectionUsesVersionTags = buySectionRoot?.dataset?.useVersionTags === 'true';
const onetimeOnlyMessage =
  buySectionRoot?.dataset?.onetimeOnlyMessage || 'one-time only';
const subscriptionOnlyMessage =
  buySectionRoot?.dataset?.subscriptionOnlyMessage || 'subscription only';
let lastNutritionSyncHandle = '';
const nutritionHtmlCache = new Map();
let nutritionPrefetchTimer = null;
const debugProductsSource = (() => {
  try {
    const el = document.querySelector('[data-buy-debug-products]');
    if (!el) return [];
    return JSON.parse(el.textContent || '[]');
  } catch {
    return [];
  }
})();
const debugVersionKeys = (() => {
  try {
    const el = document.querySelector('[data-buy-debug-versions]');
    if (!el) return [];
    return JSON.parse(el.textContent || '[]').map(v => String(v || '').toLowerCase().trim()).filter(Boolean);
  } catch {
    return [];
  }
})();
const debugPortionValues = (() => {
  try {
    const el = document.querySelector('[data-buy-debug-portions]');
    if (!el) return [];
    return JSON.parse(el.textContent || '[]')
      .map(v => Number(v))
      .filter(v => Number.isFinite(v) && v > 0);
  } catch {
    return [];
  }
})();
const debugPortionConfigs = (() => {
  try {
    const el = document.querySelector('[data-buy-debug-portions-config]');
    if (!el) return [];
    return JSON.parse(el.textContent || '[]');
  } catch {
    return [];
  }
})();
const debugPortionIdSet = new Set(
  debugPortionConfigs
    .map(item => String(item?.id || '').trim())
    .filter(Boolean)
);
const debugPortionHandleSet = new Set(
  debugPortionConfigs
    .map(item => String(item?.handle || '').toLowerCase().trim())
    .filter(Boolean)
);
const debugFlavorKeys = (() => {
  try {
    const el = document.querySelector('[data-buy-debug-flavors]');
    if (!el) return [];
    return JSON.parse(el.textContent || '[]')
      .map(v => String(v || '').toLowerCase().trim())
      .filter(Boolean);
  } catch {
    return [];
  }
})();

function escapeDebugHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getDebugBadge(label, tone = 'neutral') {
  return `<span class="buy-debug-badge buy-debug-badge--${tone}">${escapeDebugHtml(label)}</span>`;
}

const hapticsDebugState = {
  lastAttempt: 'none',
  lastPath: 'none',
  lastResult: 'idle',
  lastError: '',
  promiseObserved: false,
  promiseResolved: false,
  promiseRejected: false,
  resolvedHasTrigger: false,
  lastUpdatedAt: ''
};

function stampHapticsDebugState(patch = {}) {
  Object.assign(hapticsDebugState, patch, {
    lastUpdatedAt: new Date().toLocaleTimeString()
  });
  renderHapticsDebugBanner();
}

function observeHapticsLoadingPromise() {
  if (hapticsDebugState.promiseObserved) return;
  if (!window.__manaHapticsLoading || typeof window.__manaHapticsLoading.then !== 'function') return;

  hapticsDebugState.promiseObserved = true;
  window.__manaHapticsLoading
    .then((haptics) => {
      stampHapticsDebugState({
        promiseResolved: true,
        promiseRejected: false,
        resolvedHasTrigger: !!(haptics && typeof haptics.trigger === 'function')
      });
    })
    .catch((error) => {
      stampHapticsDebugState({
        promiseResolved: false,
        promiseRejected: true,
        resolvedHasTrigger: false,
        lastError: error?.message || String(error || 'Unknown loading error')
      });
    });
}

function getHapticsDebugMarkup() {
  observeHapticsLoadingPromise();

  const hasInstance = !!window.__manaHaptics;
  const hasTrigger = !!(window.__manaHaptics && typeof window.__manaHaptics.trigger === 'function');
  const hasLoadingPromise = !!(window.__manaHapticsLoading && typeof window.__manaHapticsLoading.then === 'function');
  const hasVibrate = !!(navigator && typeof navigator.vibrate === 'function');
  const isCoarsePointer = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  const maybeSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');

  const loaderStatus = hasInstance
    ? getDebugBadge('instance ready', 'ok')
    : hasLoadingPromise
      ? getDebugBadge('promise present', 'neutral')
      : getDebugBadge('loader missing', 'warn');

  const resolvedStatus = hapticsDebugState.promiseRejected
    ? getDebugBadge('promise rejected', 'warn')
    : hapticsDebugState.promiseResolved
      ? getDebugBadge(hapticsDebugState.resolvedHasTrigger ? 'promise resolved' : 'resolved without trigger', hapticsDebugState.resolvedHasTrigger ? 'ok' : 'warn')
      : hasLoadingPromise
        ? getDebugBadge('waiting for resolve', 'neutral')
        : '';

  return `
    <div class="buy-debug-kv"><strong>Haptics:</strong> ${loaderStatus}${resolvedStatus}</div>
    <div class="buy-debug-kv">instance global: <code>${hasInstance ? 'present' : 'missing'}</code></div>
    <div class="buy-debug-kv">trigger fn: <code>${hasTrigger ? 'yes' : 'no'}</code></div>
    <div class="buy-debug-kv">loading promise: <code>${hasLoadingPromise ? 'present' : 'missing'}</code></div>
    <div class="buy-debug-kv">navigator.vibrate: <code>${hasVibrate ? 'yes' : 'no'}</code></div>
    <div class="buy-debug-kv">pointer coarse: <code>${isCoarsePointer ? 'yes' : 'no'}</code></div>
    <div class="buy-debug-kv">Safari-like UA: <code>${maybeSafari ? 'yes' : 'no'}</code></div>
    <div class="buy-debug-kv">last attempt/path/result: <code>${escapeDebugHtml(hapticsDebugState.lastAttempt)}</code> / <code>${escapeDebugHtml(hapticsDebugState.lastPath)}</code> / <code>${escapeDebugHtml(hapticsDebugState.lastResult)}</code></div>
    ${hapticsDebugState.lastError ? `<div class="buy-debug-warn">• ${escapeDebugHtml(hapticsDebugState.lastError)}</div>` : '<div class="buy-debug-ok">Bez loader erroru.</div>'}
    <div class="buy-haptics-debug-actions">
      <button type="button" data-buy-haptics-test="selection">Test selection haptic</button>
      <button type="button" data-buy-haptics-test="success">Test success haptic</button>
    </div>
  `;
}

function bindHapticsDebugButtons(scope) {
  if (!scope) return;
  scope.querySelectorAll('[data-buy-haptics-test]').forEach((button) => {
    button.addEventListener('click', () => {
      const kind = button.getAttribute('data-buy-haptics-test');
      if (kind === 'selection') {
        triggerSelectionHaptic();
      } else {
        triggerHapticFeedback();
      }
    });
  });
}

function renderHapticsDebugBanner() {
  if (!GALLERY_DEBUG) return;

  const panel = document.querySelector('[data-buy-debug-panel]');
  const body = document.querySelector('[data-buy-debug-body]');
  const ts = document.querySelector('[data-buy-debug-timestamp]');
  if (!panel || !body) return;

  panel.hidden = false;
  if (ts) ts.textContent = hapticsDebugState.lastUpdatedAt || new Date().toLocaleTimeString();
}

function getShopPathPrefix() {
  const match = window.location.pathname.match(/^(.*)\/products\/[^/]+/);
  return match && match[1] ? match[1] : '';
}

function applyNutritionHtml(payload) {
  if (!payload) return;
  const target = document.getElementById('nutrition_information_buy_popup');
  if (target && typeof payload.visual === 'string') target.innerHTML = payload.visual;
}

async function ensureNutritionForHandle(handle, { apply = true } = {}) {
  const targetHandle = String(handle || '').trim();
  if (!targetHandle) return;

  if (nutritionHtmlCache.has(targetHandle)) {
    const cached = nutritionHtmlCache.get(targetHandle);
    if (apply) applyNutritionHtml(cached);
    lastNutritionSyncHandle = targetHandle;
    return;
  }

  const productUrl = `${getShopPathPrefix()}/products/${targetHandle}`;
  const response = await fetch(productUrl, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`nutrition fetch failed: ${response.status}`);

  const html = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const payload = {
    visual: doc.getElementById('nutrition_information_buy_popup')?.innerHTML || ''
  };

  nutritionHtmlCache.set(targetHandle, payload);
  if (apply) applyNutritionHtml(payload);
  lastNutritionSyncHandle = targetHandle;
}

function prefetchNutritionForSelection(version, portions) {
  if (!buySectionRoot) return;
  if (nutritionPrefetchTimer) {
    clearTimeout(nutritionPrefetchTimer);
    nutritionPrefetchTimer = null;
  }

  nutritionPrefetchTimer = setTimeout(() => {
    const handles = Array.from(document.querySelectorAll('.flavour-option'))
      .filter(option =>
        !option.classList.contains('is-hidden') &&
        (!version || option.dataset.version === String(version)) &&
        (!portions || option.dataset.portions === String(portions))
      )
      .map(option => String(option.dataset.productHandle || '').trim())
      .filter(Boolean);

    const uniqueHandles = Array.from(new Set(handles)).slice(0, 4);
    uniqueHandles.forEach((handle) => {
      ensureNutritionForHandle(handle, { apply: false }).catch((error) => {
        gDebug('nutrition prefetch failed', { handle, error });
      });
    });
  }, 350);
}

function renderBuyDebugPanel(reason = 'update') {
  if (!GALLERY_DEBUG) return;

  const panel = document.querySelector('[data-buy-debug-panel]');
  const body = document.querySelector('[data-buy-debug-body]');
  const ts = document.querySelector('[data-buy-debug-timestamp]');
  if (!panel || !body) return;

  panel.hidden = false;
  if (ts) ts.textContent = `${new Date().toLocaleTimeString()} • ${reason}`;

  const activeVersionBtn = document.querySelector('.category-product[aria-pressed="true"]');
  const activePackageBtn = document.querySelector('.package-product[aria-pressed="true"]');
  const flavourOptions = Array.from(document.querySelectorAll('.flavour-option[data-variant-id]'));
  const useVersionTags = buySectionRoot?.dataset?.useVersionTags === 'true';
  const versionFallbackMode = buySectionRoot?.dataset?.versionFallbackMode === 'true';
  const visibleFlavours = flavourOptions.filter(opt => !opt.classList.contains('is-hidden'));
  const selectedFlavours = visibleFlavours.filter(opt => {
    const qtyInput = opt.querySelector('.quantity-input');
    return (parseInt(qtyInput?.value || '0', 10) || 0) > 0;
  });

  const globalWarnings = [];
  if (!activeVersionBtn) globalWarnings.push('Není aktivní verze (category-product).');
  if (!activePackageBtn) globalWarnings.push('Není aktivní velikost balení (package-product).');
  if (!visibleFlavours.length) globalWarnings.push('Pro aktivní kombinaci není žádná příchuť.');
  if (buySectionRoot?.dataset?.hasSwitchingCollection !== 'true') {
    globalWarnings.push('Nenašla se switching kolekce. Zkontroluj metafield kolekce buy_section.collection=true a že kolekce je publikovaná v Online Store sales channelu.');
  }
  if (versionFallbackMode) {
    globalWarnings.push('Buy sekce běží ve fallback version režimu. To znamená, že buysection.version je na switching kolekci prázdné nebo ho Liquid nevidí, takže se nenačtou version features.');
  }

  const sourceProducts = Array.isArray(debugProductsSource) ? debugProductsSource : [];
  const sourceMissingVersionTag = sourceProducts.filter(productItem => {
    const tags = Array.isArray(productItem.tags) ? productItem.tags : [];
    if (!useVersionTags || !debugVersionKeys.length) return false;
    return !tags.some(tag => debugVersionKeys.includes(String(tag || '').toLowerCase().trim()));
  });
  const sourceMissingFlavor = sourceProducts.filter(productItem => !String(productItem.flavor || '').trim());
  const sourceMissingServings = sourceProducts.filter(productItem => {
    const servings = String(productItem.servings || '').trim();
    return !servings || Number.isNaN(Number(servings));
  });
  const sourceMissingVariant = sourceProducts.filter(productItem => !String(productItem.variantId || '').trim());

  if (sourceMissingVersionTag.length) {
    globalWarnings.push(`Produkty bez version tagu: ${sourceMissingVersionTag.length}`);
  }
  if (sourceMissingFlavor.length) {
    globalWarnings.push(`Produkty bez metafieldu flavor: ${sourceMissingFlavor.length}`);
  }
  if (sourceMissingServings.length) {
    globalWarnings.push(`Produkty bez validního metafieldu servings: ${sourceMissingServings.length}`);
  }
  if (sourceMissingVariant.length) {
    globalWarnings.push(`Produkty bez varianty: ${sourceMissingVariant.length}`);
  }
  const sourceMissingPrice = sourceProducts.filter(productItem => Number(productItem.priceCents || 0) <= 0);
  if (sourceMissingPrice.length) {
    globalWarnings.push(`Produkty bez ceny: ${sourceMissingPrice.length}`);
  }
  const sourcePackageTypeNotInConfig = sourceProducts.filter(productItem => {
    const packageTypeId = String(productItem.packageTypeId || '').trim();
    const packageTypeHandle = String(productItem.packageType || '').toLowerCase().trim();
    if (!packageTypeId && !packageTypeHandle) return false;
    const hasIdMatch = packageTypeId && debugPortionIdSet.has(packageTypeId);
    const hasHandleMatch = packageTypeHandle && debugPortionHandleSet.has(packageTypeHandle);
    return !(hasIdMatch || hasHandleMatch);
  });
  if (sourcePackageTypeNotInConfig.length) {
    globalWarnings.push(`Produkty s package_type mimo buysection.portions: ${sourcePackageTypeNotInConfig.length}`);
  }
  const sourceFlavorNotInConfig = sourceProducts.filter(productItem => {
    const flavor = String(productItem.flavor || '').toLowerCase().trim();
    if (!flavor || !debugFlavorKeys.length) return false;
    return !debugFlavorKeys.includes(flavor);
  });
  if (sourceFlavorNotInConfig.length) {
    globalWarnings.push(`Flavor mimo buysection.flavor: ${sourceFlavorNotInConfig.length}`);
  }
  const sourceServingsNotInConfig = sourceProducts.filter(productItem => {
    const servings = Number(productItem.servings);
    if (!Number.isFinite(servings) || !debugPortionValues.length) return false;
    return !debugPortionValues.includes(servings);
  });
  if (sourceServingsNotInConfig.length) {
    globalWarnings.push(`Servings mimo buysection.portions: ${sourceServingsNotInConfig.length}`);
  }
  if (petDepositEnabled && !hasBottlePrice) {
    globalWarnings.push('Chybí shop metafield sga.bottlePrice nebo je mimo povolenou doménu.');
  }

  const flavourItems = visibleFlavours.map(opt => {
    const name = (opt.querySelector('.h2')?.textContent || opt.dataset.flavor || 'Unknown').trim();
    const qty = parseInt(opt.querySelector('.quantity-input')?.value || '0', 10) || 0;
    const warnings = [];
    const variantId = opt.dataset.variantId || '';
    const imagesRaw = opt.dataset.productImages || '[]';
    let images = [];
    try { images = JSON.parse(imagesRaw); } catch { images = []; }

    if (!variantId) warnings.push('Chybí variant ID.');
    if (!opt.dataset.priceCents) warnings.push('Chybí data-price-cents.');
    if (!opt.dataset.version) warnings.push('Chybí data-version.');
    if (!opt.dataset.portions) warnings.push('Chybí data-portions.');
    if (!Array.isArray(images) || !images.length) warnings.push('Chybí obrázky galerie.');
    if (opt.dataset.subscriptionOnly === 'true' && opt.dataset.onetimeOnly === 'true') {
      warnings.push('Konflikt tagů: subscription-only + onetime/preorder.');
    }
    if (
      opt.dataset.subscriptionOnly !== 'true' &&
      opt.dataset.onetimeOnly !== 'true' &&
      parseInt(opt.dataset.subscriptionPriceCents || '0', 10) <= 0
    ) {
      warnings.push('Produkt není označen jako one-time only, ale nemá subscription cenu.');
    }

    const badges = [
      qty > 0 ? getDebugBadge(`qty ${qty}`, 'ok') : getDebugBadge('qty 0', 'neutral'),
      opt.dataset.available === 'false' ? getDebugBadge('soldout', 'warn') : getDebugBadge('available', 'ok'),
      opt.dataset.onetimeOnly === 'true' ? getDebugBadge('onetime/preorder', 'warn') : '',
      opt.dataset.subscriptionOnly === 'true' ? getDebugBadge('sub-only', 'warn') : '',
      parseInt(opt.dataset.subscriptionPriceCents || '0', 10) > 0 ? getDebugBadge('sub price', 'ok') : getDebugBadge('no sub price', 'warn')
    ].filter(Boolean).join('');

    return `
      <details class="buy-debug-group" ${qty > 0 || warnings.length ? 'open' : ''}>
        <summary>${escapeDebugHtml(name)} ${badges}</summary>
        <div class="buy-debug-kv">handle: <code>${escapeDebugHtml(opt.dataset.productHandle || 'N/A')}</code></div>
        <div class="buy-debug-kv">variant: <code>${escapeDebugHtml(variantId || 'N/A')}</code></div>
        <div class="buy-debug-kv">verze/balení: <code>${escapeDebugHtml(opt.dataset.version || '?')}</code> / <code>${escapeDebugHtml(opt.dataset.portions || '?')}</code></div>
        <div class="buy-debug-kv">flavor key: <code>${escapeDebugHtml(opt.dataset.flavor || 'N/A')}</code></div>
        <div class="buy-debug-kv">ceny: one-time <code>${escapeDebugHtml(opt.dataset.priceCents || '0')}</code>, sub <code>${escapeDebugHtml(opt.dataset.subscriptionPriceCents || '0')}</code></div>
        <div class="buy-debug-kv">selling_plan: <code>${escapeDebugHtml(opt.dataset.sellingPlanId || 'N/A')}</code></div>
        <div class="buy-debug-kv">weight grams: <code>${escapeDebugHtml(opt.dataset.weightGrams || '0')}</code></div>
        <div class="buy-debug-kv">images: <code>${images.length}</code></div>
        ${warnings.length ? `<div class="buy-debug-warn">${warnings.map(w => `• ${escapeDebugHtml(w)}`).join('<br>')}</div>` : '<div class="buy-debug-ok">Bez warningů.</div>'}
      </details>
    `;
  }).join('');

  const sourceIntegrityItems = sourceProducts.map(productItem => {
    const tags = Array.isArray(productItem.tags) ? productItem.tags : [];
    const hardBlockers = [];
    const softWarnings = [];
    const hasVersionTag = tags.some(tag => debugVersionKeys.includes(String(tag || '').toLowerCase().trim()));
    const hasFlavor = !!String(productItem.flavor || '').trim();
    const servingsNum = Number(productItem.servings);
    const hasServings = !!String(productItem.servings || '').trim() && Number.isFinite(servingsNum);
    const hasVariant = !!String(productItem.variantId || '').trim();
    const hasPrice = Number(productItem.priceCents || 0) > 0;
    const subscriptionPriceCents = Number(productItem.subscriptionPriceCents || 0);
    const hasSubscriptionPrice = Number.isFinite(subscriptionPriceCents) && subscriptionPriceCents > 0;
    const subscriptionReason = String(productItem.subscriptionReason || '').trim();
    const onetimeByTag = productItem.onetimeByTag === true || String(productItem.onetimeByTag || '') === 'true';
    const hasSubscriptionGroup = productItem.hasSubscriptionGroup === true || String(productItem.hasSubscriptionGroup || '') === 'true';
    const hasSubscriptionAllocation = productItem.hasSubscriptionAllocation === true || String(productItem.hasSubscriptionAllocation || '') === 'true';
    const subscriptionGroups = Array.isArray(productItem.subscriptionGroups) ? productItem.subscriptionGroups : [];
    const flavorKey = String(productItem.flavor || '').toLowerCase().trim();
    const isFlavorConfigured = !debugFlavorKeys.length || (flavorKey && debugFlavorKeys.includes(flavorKey));
    const isServingsConfigured = !debugPortionValues.length || (Number.isFinite(servingsNum) && debugPortionValues.includes(servingsNum));

    if (useVersionTags && !hasVersionTag) hardBlockers.push('Chybí version tag.');
    if (!hasFlavor) hardBlockers.push('Chybí metafield productFields.flavor.');
    if (!hasServings) hardBlockers.push('Chybí nebo nečíselný metafield productFields.servings.');
    if (!hasVariant) hardBlockers.push('Produkt nemá selected_or_first_available_variant.');
    if (!hasPrice) hardBlockers.push('Produkt nemá cenu (price <= 0).');
    if (hasFlavor && !isFlavorConfigured) hardBlockers.push('Flavor není v buysection.flavor.');
    if (hasServings && !isServingsConfigured) hardBlockers.push('Servings není v buysection.portions.');
    const packageTypeId = String(productItem.packageTypeId || '').trim();
    const packageTypeHandle = String(productItem.packageType || '').toLowerCase().trim();
    const hasPackageType = Boolean(packageTypeId || packageTypeHandle);
    const isPackageTypeConfigured =
      !hasPackageType ||
      (packageTypeId && debugPortionIdSet.has(packageTypeId)) ||
      (packageTypeHandle && debugPortionHandleSet.has(packageTypeHandle));
    if (hasPackageType && !isPackageTypeConfigured) {
      hardBlockers.push('package_type není v buysection.portions.');
    }
    if (!onetimeByTag && !hasSubscriptionPrice) {
      softWarnings.push(`Chybí subscription cena (${subscriptionReason || 'neznámý důvod'}).`);
    }

    const subscriptionGroupsMarkup = subscriptionGroups.length
      ? subscriptionGroups.map((group) => {
          const groupName = String(group?.name || 'Unnamed group').trim();
          const plansCount = Number(group?.plansCount || 0);
          const isSelected = group?.selected === true || String(group?.selected || '') === 'true';
          const isIgnored = group?.ignored === true || String(group?.ignored || '') === 'true';
          const ignoreReason = String(group?.ignoreReason || '').trim();
          const badges = [
            isSelected ? getDebugBadge('used', 'ok') : '',
            isIgnored ? getDebugBadge('ignored', 'warn') : '',
            !isSelected && !isIgnored ? getDebugBadge('available', 'neutral') : '',
            getDebugBadge(`${plansCount} plans`, 'neutral')
          ].filter(Boolean).join(' ');

          return `<div class="buy-debug-kv">• <code>${escapeDebugHtml(groupName)}</code> ${badges}${ignoreReason ? ` <span>(${escapeDebugHtml(ignoreReason)})</span>` : ''}</div>`;
        }).join('')
      : '<div class="buy-debug-kv">Žádné Smartrr plan groups.</div>';

    const imagesCount = Array.from(document.querySelectorAll('.flavour-option[data-product-handle]'))
      .filter(opt => opt.dataset.productHandle === productItem.handle)
      .map(opt => {
        try { return JSON.parse(opt.dataset.productImages || '[]').length; } catch { return 0; }
      })
      .reduce((max, count) => Math.max(max, count), 0);
    if (imagesCount === 0) softWarnings.push('Nebyla nalezena galerie obrázků.');
    if (productItem.available === false) softWarnings.push('Produkt je neprodejný (available=false).');

    const fixSteps = [];
    if (useVersionTags && !hasVersionTag) fixSteps.push('Přidej product tag jedné z buysection verzí.');
    if (!hasFlavor) fixSteps.push('Vyplň metafield productFields.flavor.');
    if (hasFlavor && !isFlavorConfigured) fixSteps.push('Doplň flavor do buysection.flavor nebo uprav metafield flavor.');
    if (!hasServings) fixSteps.push('Vyplň číselný metafield productFields.servings.');
    if (hasServings && !isServingsConfigured) fixSteps.push('Přidej servings hodnotu do buysection.portions.');
    if (!hasVariant) fixSteps.push('Vytvoř variantu produktu (selected_or_first_available_variant).');
    if (!hasPrice) fixSteps.push('Nastav cenu produktu (>0).');
    if (hasPackageType && !isPackageTypeConfigured) fixSteps.push('Přidej package_type metaobjekt do buysection.portions kolekce.');
    if (!imagesCount) fixSteps.push('Přidej alespoň 1 produktový obrázek.');
    const renderReady = hardBlockers.length === 0;

    return `
      <details class="buy-debug-group" ${hardBlockers.length ? 'open' : ''}>
        <summary>${escapeDebugHtml(productItem.title || productItem.handle || 'Unknown product')}
          ${renderReady ? getDebugBadge('RENDER READY', 'ok') : getDebugBadge(`${hardBlockers.length} BLOCKER`, 'warn')}
          ${softWarnings.length ? getDebugBadge(`${softWarnings.length} warn`, 'neutral') : ''}
        </summary>
        <div class="buy-debug-kv">handle: <code>${escapeDebugHtml(productItem.handle || 'N/A')}</code></div>
        <div class="buy-debug-kv">variant: <code>${escapeDebugHtml(productItem.variantId || 'N/A')}</code></div>
        <div class="buy-debug-kv">price: <code>${escapeDebugHtml(productItem.priceCents || '0')}</code></div>
        <div class="buy-debug-kv">subscription price:
          ${hasSubscriptionPrice
            ? `<code>${escapeDebugHtml(String(subscriptionPriceCents))}</code> ${getDebugBadge('OK', 'ok')}`
            : `<code>N/A</code> ${getDebugBadge(onetimeByTag ? 'onetime/preorder' : (subscriptionReason || 'missing'), onetimeByTag ? 'neutral' : 'warn')}`}
        </div>
        <div class="buy-debug-kv">subscription source:
          <code>group ${hasSubscriptionGroup ? 'OK' : 'MISS'}</code>,
          <code>allocation ${hasSubscriptionAllocation ? 'OK' : 'MISS'}</code>
        </div>
        <div class="buy-debug-kv"><strong>subscription groups:</strong></div>
        ${subscriptionGroupsMarkup}
        <div class="buy-debug-kv">flavor / servings: <code>${escapeDebugHtml(productItem.flavor || 'N/A')}</code> / <code>${escapeDebugHtml(productItem.servings || 'N/A')}</code></div>
        <div class="buy-debug-kv">package_type id/handle: <code>${escapeDebugHtml(productItem.packageTypeId || 'N/A')}</code> / <code>${escapeDebugHtml(productItem.packageType || 'N/A')}</code></div>
        <div class="buy-debug-kv">package_type.value (json): <code>${escapeDebugHtml(productItem.packageTypeValueJson || 'null')}</code></div>
        <div class="buy-debug-kv">dietary label / taxonomy_reference: <code>${escapeDebugHtml(productItem.dietaryLabel || 'N/A')}</code> / <code>${escapeDebugHtml(productItem.dietaryTaxonomyReference || 'N/A')}</code></div>
        <div class="buy-debug-kv">caffeine-free match: <code>${productItem.caffeinefree ? 'YES' : 'NO'}</code></div>
        <div class="buy-debug-kv">tags: <code>${escapeDebugHtml(tags.join(', ') || 'none')}</code></div>
        <div class="buy-debug-kv">match check:
          <code>version ${useVersionTags ? (hasVersionTag ? 'OK' : 'FAIL') : 'SKIP'}</code>,
          <code>flavor ${hasFlavor ? (isFlavorConfigured ? 'OK' : 'FAIL') : 'FAIL'}</code>,
          <code>servings ${hasServings ? (isServingsConfigured ? 'OK' : 'FAIL') : 'FAIL'}</code>,
          <code>variant ${hasVariant ? 'OK' : 'FAIL'}</code>
        </div>
        ${hardBlockers.length ? `<div class="buy-debug-warn"><strong>Hard blockers:</strong><br>${hardBlockers.map(w => `• ${escapeDebugHtml(w)}`).join('<br>')}</div>` : '<div class="buy-debug-ok">Bez hard blockerů.</div>'}
        ${softWarnings.length ? `<div class="buy-debug-kv"><strong>Soft warnings:</strong><br>${softWarnings.map(w => `• ${escapeDebugHtml(w)}`).join('<br>')}</div>` : ''}
        ${fixSteps.length ? `<div class="buy-debug-kv"><strong>How to fix:</strong><br>${fixSteps.map(w => `• ${escapeDebugHtml(w)}`).join('<br>')}</div>` : ''}
      </details>
    `;
  }).join('');

  const hardBlockerCount = sourceProducts.filter(productItem => {
    const tags = Array.isArray(productItem.tags) ? productItem.tags : [];
    const hasVersionTag = tags.some(tag => debugVersionKeys.includes(String(tag || '').toLowerCase().trim()));
    const hasFlavor = !!String(productItem.flavor || '').trim();
    const servingsNum = Number(productItem.servings);
    const hasServings = !!String(productItem.servings || '').trim() && Number.isFinite(servingsNum);
    const hasVariant = !!String(productItem.variantId || '').trim();
    const hasPrice = Number(productItem.priceCents || 0) > 0;
    const flavorKey = String(productItem.flavor || '').toLowerCase().trim();
    const isFlavorConfigured = !debugFlavorKeys.length || (flavorKey && debugFlavorKeys.includes(flavorKey));
    const isServingsConfigured = !debugPortionValues.length || (Number.isFinite(servingsNum) && debugPortionValues.includes(servingsNum));
    return (useVersionTags && !hasVersionTag) || !hasFlavor || !hasServings || !hasVariant || !hasPrice || !isFlavorConfigured || !isServingsConfigured;
  }).length;

  const galleryTrack = document.querySelector('.product-gallery-track');
  const galleryImgs = galleryTrack ? Array.from(galleryTrack.querySelectorAll('img')) : [];
  const activeGallery = galleryImgs.filter(img => img.classList.contains('is-active')).length;
  const visiblePriceValues = new Set();
  visibleFlavours.forEach(opt => {
    const oneTime = parseInt(opt.dataset.priceCents || '0', 10);
    const sub = parseInt(opt.dataset.subscriptionPriceCents || '0', 10);
    if (oneTime > 0) visiblePriceValues.add(oneTime);
    if (sub > 0) visiblePriceValues.add(sub);
  });
  const hasMultiplePriceVariants = visiblePriceValues.size > 1;

  body.innerHTML = `
    <details class="buy-debug-group">
      <summary>System ${getDebugBadge(`mode ${purchaseTypeState.type}`, 'neutral')}</summary>
      <div class="buy-debug-kv">aktivní verze: <code>${escapeDebugHtml(activeVersionBtn?.dataset?.version || 'N/A')}</code></div>
      <div class="buy-debug-kv">aktivní balení: <code>${escapeDebugHtml(activePackageBtn?.dataset?.portions || 'N/A')}</code></div>
      <div class="buy-debug-kv">flavours visible/selected: <code>${visibleFlavours.length}</code> / <code>${selectedFlavours.length}</code></div>
      <div class="buy-debug-kv">gallery img/active: <code>${galleryImgs.length}</code> / <code>${activeGallery}</code></div>
      <div class="buy-debug-kv">price combinations in current view: <code>${visiblePriceValues.size}</code> (${hasMultiplePriceVariants ? 'zobraz "od"' : 'bez "od"'})</div>
      <div class="buy-debug-kv">config mode:
        <code>version tags ${useVersionTags ? 'ON' : 'OFF'}</code>,
        <code>version fallback ${versionFallbackMode ? 'ON' : 'OFF'}</code>,
        <code>pet deposit ${petDepositEnabled ? 'ON' : 'OFF'}</code>,
        <code>pet tags ${escapeDebugHtml(petDepositTags.join(', ') || 'none')}</code>,
        <code>bottle price ${hasBottlePrice ? 'OK' : 'MISSING'}</code>,
        <code>bottle price cents ${bottlePriceCents}</code>,
        <code>initial version ${escapeDebugHtml(buySectionRoot?.dataset?.initialVersion || 'N/A')}</code>,
        <code>flavor keys ${debugFlavorKeys.length}</code>,
        <code>portions ${debugPortionValues.length}</code>
      </div>
      ${getHapticsDebugMarkup()}
      ${globalWarnings.length ? `<div class="buy-debug-warn">${globalWarnings.map(w => `• ${escapeDebugHtml(w)}`).join('<br>')}</div>` : '<div class="buy-debug-ok">Bez systémových warningů.</div>'}
    </details>
    <details class="buy-debug-group">
      <summary>Hard Blockers ${getDebugBadge(`${hardBlockerCount} blocked`, hardBlockerCount ? 'warn' : 'ok')}</summary>
      <div class="buy-debug-kv">Produkt s hard blockerem se nepropíše správně do flavour listu.</div>
      <div class="buy-debug-kv">Kontroluj: <code>version tag</code>, <code>buysection.version</code>, <code>productFields.flavor</code>, <code>productFields.servings</code>, <code>variant</code>, <code>price</code>, mapování do <code>buysection.flavor/portions</code>.</div>
    </details>
    <details class="buy-debug-group">
      <summary>Data Integrity ${getDebugBadge(`${sourceProducts.length} source products`, 'neutral')}</summary>
      ${sourceIntegrityItems || '<div class="buy-debug-kv">Žádné source produkty.</div>'}
    </details>
    <details class="buy-debug-group">
      <summary>Produkty / příchutě ${getDebugBadge(`${visibleFlavours.length} viditelných`, 'neutral')}</summary>
      ${flavourItems || '<div class="buy-debug-kv">Žádné viditelné příchutě.</div>'}
    </details>
  `;

  bindHapticsDebugButtons(body);
}

function normalizePackageHandle(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizePackageId(value) {
  return String(value || '').trim();
}

function isFlavourOptionMatch(option, version, portions, packageHandle = '', packageId = '') {
  if (!option || option.dataset.version !== String(version)) return false;

  const activePackageId = normalizePackageId(packageId);
  const optionPackageId = normalizePackageId(option.dataset.packageId);
  const activePackageHandle = normalizePackageHandle(packageHandle);
  const optionPackageHandle = normalizePackageHandle(option.dataset.packageHandle);

  if (activePackageId) {
    if (optionPackageId) {
      return optionPackageId === activePackageId;
    }
    return option.dataset.portions === String(portions);
  }

  if (activePackageHandle) {
    if (optionPackageHandle) {
      return optionPackageHandle === activePackageHandle;
    }
    return option.dataset.portions === String(portions);
  }

  return option.dataset.portions === String(portions);
}

function getVisibleFlavourOptions(version, portions, packageHandle = '', packageId = '') {
  return Array.from(document.querySelectorAll('.flavour-option'))
    .filter(opt =>
      !opt.classList.contains('is-hidden') &&
      isFlavourOptionMatch(opt, version, portions, packageHandle, packageId)
    );
}

function updateGlutenFree(activeVersion, activePortions, activePackageHandle = '', activePackageId = '') {
  const gluten = document.querySelector('[data-feature-glutenfree]');
  const glutenWrap = document.querySelector('[data-feature-glutenfree-wrap]');
  if (!gluten) return;

  const visible =
    activeVersion && activePortions
      ? getVisibleFlavourOptions(
          String(activeVersion),
          String(activePortions),
          String(activePackageHandle || ''),
          String(activePackageId || '')
        )
      : [];

  const first = visible[0];
  const shouldShow = !!(first && first.dataset.glutenfree === 'true');
  const hasGsap = typeof window.gsap !== 'undefined';
  const wrapHidden = glutenWrap ? glutenWrap.classList.contains('is-hidden') : false;
  const iconHidden = gluten.classList.contains('is-hidden');
  const isVisibleNow = !wrapHidden && !iconHidden;

  if (shouldShow === isVisibleNow) return;

  if (!glutenWrap || !hasGsap) {
    if (glutenWrap) {
      glutenWrap.classList.toggle('is-hidden', !shouldShow);
    }
    gluten.classList.toggle('is-hidden', !shouldShow);
    return;
  }

  window.gsap.killTweensOf(glutenWrap);
  window.gsap.killTweensOf(gluten);

  const getExpandedWidth = () => {
    const stored = Number(glutenWrap.dataset.expandedWidth || 0);
    if (stored > 0) return stored;
    const measured = Math.ceil(glutenWrap.getBoundingClientRect().width);
    return measured > 0 ? measured : 136;
  };
  const expandedWidth = getExpandedWidth();

  if (shouldShow) {
    glutenWrap.classList.remove('is-hidden');
    gluten.classList.remove('is-hidden');

    window.gsap.set(glutenWrap, {
      width: 0,
      marginLeft: -30,
      opacity: 0
    });
    window.gsap.set(gluten, {
      x: 24,
      opacity: 0
    });

    const showTl = window.gsap.timeline();
    showTl.to(glutenWrap, {
      width: expandedWidth,
      marginLeft: 0,
      opacity: 1,
      duration: 0.34,
      ease: 'power2.out'
    }, 0);
    showTl.to(gluten, {
      x: 0,
      opacity: 1,
      duration: 0.26,
      ease: 'power2.out'
    }, 0.02);
    showTl.eventCallback('onComplete', () => {
      glutenWrap.style.width = `${expandedWidth}px`;
      glutenWrap.dataset.expandedWidth = String(expandedWidth);
      glutenWrap.style.removeProperty('margin-left');
      glutenWrap.style.removeProperty('opacity');
      gluten.style.removeProperty('transform');
      gluten.style.removeProperty('opacity');
    });
    return;
  }

  glutenWrap.classList.remove('is-hidden');
  gluten.classList.remove('is-hidden');
  const currentWidth = Math.ceil(glutenWrap.getBoundingClientRect().width);
  if (currentWidth > 0) {
    glutenWrap.dataset.expandedWidth = String(currentWidth);
  }
  window.gsap.set(glutenWrap, {
    width: currentWidth > 0 ? currentWidth : expandedWidth,
    marginLeft: 0,
    opacity: 1
  });
  window.gsap.set(gluten, {
    x: 0,
    opacity: 1
  });

  const hideTl = window.gsap.timeline({
    onComplete: () => {
      gluten.classList.add('is-hidden');
      glutenWrap.classList.add('is-hidden');
      glutenWrap.style.removeProperty('width');
      glutenWrap.style.removeProperty('margin-left');
      glutenWrap.style.removeProperty('opacity');
      gluten.style.removeProperty('transform');
      gluten.style.removeProperty('opacity');
    }
  });

  // Move/fade icon and collapse wrapper at the same time so neighbors center smoothly.
  hideTl.to(gluten, {
    x: 24,
    opacity: 0,
    duration: 0.24,
    ease: 'power1.in'
  }, 0);
  hideTl.to(glutenWrap, {
    width: 0,
    marginLeft: -30,
    opacity: 0,
    duration: 0.30,
    ease: 'power1.inOut'
  }, 0);
}

function animateFeatures(versionKey) {
  if (typeof window.gsap === 'undefined') return;
  // Find the active version button
  const activeBtn = document.querySelector('.category-product[aria-pressed="true"]');
  if (!activeBtn) return;
  const protein = activeBtn.getAttribute('data-protein');
  const carbs = activeBtn.getAttribute('data-carbs');
  const kcal = activeBtn.getAttribute('data-kcal');
  // Don't animate if any required value is missing
  if (!protein || !carbs || !kcal) return;
  // Animate each feature
  const featureMap = {
    protein: protein,
    carbs: carbs,
    fat: kcal
  };
  document.querySelectorAll('.feature .number').forEach(el => {
    const key = el.dataset.feature;
    const targetStr = featureMap[key];
    if (!targetStr) return;
    const target = parseFloat(targetStr);
    if (isNaN(target)) return;
    const from = parseFloat(el.textContent) || 0;
    const counter = { value: from };
    gsap.to(counter, {
      value: target,
      duration: 0.8,
      ease: 'power2.out',
      onUpdate: () => {
        el.textContent = Math.round(counter.value);
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
    // === INIT PURCHASE TYPE FROM THEME SETTING (LIGHTWEIGHT) ===
    const buySectionRoot = document.querySelector('.buy-section');
    renderHapticsDebugBanner();
    const autoAddProductEnabled = buySectionRoot?.dataset?.buyAutoAddProduct === 'true';
    const currentProductHandle = (buySectionRoot?.dataset?.currentProductHandle || '').trim();
    if (buySectionRoot && buySectionRoot.dataset.subscriptionFirst) {
        purchaseTypeState.type =
            buySectionRoot.dataset.subscriptionFirst === 'true'
                ? 'subscription'
                : 'onetime';
    }
    const versionButtons = document.querySelectorAll('.category-product[data-version]');
    const packageButtons = document.querySelectorAll('.package-product[data-version]');
    const flavourOptions = document.querySelectorAll('.flavour-selector .flavour-option[data-version][data-portions]');
    let videoGalleryPin = null;
    let videoGalleryPinResizeTimer = null;
    let videoGalleryPinViewportWidth = window.innerWidth;

    function destroyVideoGalleryPin() {
      if (videoGalleryPin) {
        videoGalleryPin.kill();
        videoGalleryPin = null;
      }
      window.dispatchEvent(new CustomEvent('buy-section:video-gallery-pin', { detail: { active: false } }));
      const shell = document.querySelector('[data-product-gallery-shell]');
      if (shell) {
        shell.classList.remove('has-video-pin');
        shell.closest('.column-left')?.classList.remove('has-video-pin');
        shell.style.removeProperty('transform');
        shell.style.removeProperty('inset');
        shell.style.removeProperty('position');
        shell.style.removeProperty('width');
      }
      if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
      }
    }

    function isVideoGalleryActive() {
      const video = document.querySelector('.product-gallery-video.is-active:not(.is-hidden)');
      return !!(video && (video.currentSrc || video.src));
    }

    function getVideoGalleryHeaderOffset() {
      const selectors = [
        '#shopify-section-header',
        '#shopify-section-mana-header',
        '.shopify-section-header',
        '.site-header',
        '.header-wrapper',
        'header[role="banner"]',
        'header'
      ];

      for (const selector of selectors) {
        const el = document.querySelector(selector);
        if (!el) continue;

        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        const isVisible = rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
        const isViewportHeader = style.position === 'fixed' || style.position === 'sticky' || rect.top <= 1;

        if (isVisible && isViewportHeader) {
          const mobileAdjustment = window.matchMedia?.('(max-width: 768px)').matches ? 20 : 0;
          return Math.max(0, Math.min(140, Math.ceil(rect.height)) - mobileAdjustment);
        }
      }

      return 0;
    }

    function createVideoGalleryPin() {
      if (videoGalleryPin) return;

      const shell = document.querySelector('[data-product-gallery-shell]');
      const section = document.querySelector('.category-info');
      if (!shell || !section) return;

      window.dispatchEvent(new CustomEvent('buy-section:video-gallery-pin', { detail: { active: true } }));
      shell.classList.add('has-video-pin');
      shell.closest('.column-left')?.classList.add('has-video-pin');

      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;

      window.gsap.registerPlugin(window.ScrollTrigger);
      videoGalleryPin = window.ScrollTrigger.create({
        trigger: section,
        start: () => `top top+=${getVideoGalleryHeaderOffset()}`,
        end: () => {
          const distance = Math.max(1, section.offsetHeight - window.innerHeight + getVideoGalleryHeaderOffset());
          return `+=${distance}`;
        },
        pin: shell,
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true
      });
      window.ScrollTrigger.refresh();
    }

    function rebuildVideoGalleryPin() {
      if (!isVideoGalleryActive()) {
        destroyVideoGalleryPin();
        return;
      }
      destroyVideoGalleryPin();
      requestAnimationFrame(() => {
        createVideoGalleryPin();
      });
    }

    window.addEventListener('resize', () => {
      const isMobileViewport = window.matchMedia?.('(max-width: 768px)').matches;
      const nextWidth = window.innerWidth;
      const widthChanged = Math.abs(nextWidth - videoGalleryPinViewportWidth) > 2;

      if (isMobileViewport && !widthChanged) return;

      videoGalleryPinViewportWidth = nextWidth;
      if (videoGalleryPinResizeTimer) clearTimeout(videoGalleryPinResizeTimer);
      videoGalleryPinResizeTimer = setTimeout(rebuildVideoGalleryPin, 160);
    });

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        videoGalleryPinViewportWidth = window.innerWidth;
        rebuildVideoGalleryPin();
      }, 180);
    });

    // preload everything in background (batched)
    preloadGalleryUrls(collectGalleryImageUrls());

    document.querySelectorAll('.flavour-option').forEach(option => {
      const trigger = option.querySelector('.flavour-left');
      const desc = option.querySelector('[data-flavour-description]');
      if (!trigger || !desc) return;

      let expanded = false;

      trigger.addEventListener('click', () => {
        expanded = !expanded;

        if (expanded) {
          const h = desc.scrollHeight;
          desc.style.height = h + 'px';
          desc.classList.add('productOption__package_full--expanded');
        } else {
          desc.style.height = desc.scrollHeight + 'px';
          requestAnimationFrame(() => {
            desc.style.height = '0px';
          });
          desc.classList.remove('productOption__package_full--expanded');
        }
      });
    });


    if (!versionButtons.length) {
      if (GALLERY_DEBUG) {
        renderBuyDebugPanel('init:no-version-buttons');
      }
      return;
    }

    function updateFlavourBorders() {
        const all = Array.from(document.querySelectorAll('.flavour-option'));
        const visible = all.filter(el => !el.classList.contains('is-hidden'));

        all.forEach(el => el.classList.remove('is-last-visible'));

        if (visible.length) {
            visible[visible.length - 1].classList.add('is-last-visible');
        }
    }

    function updateFlavours(version, portions, packageHandle = '', packageId = '') {
        if (!version || !portions) return;

        flavourOptions.forEach(opt => {
            const match = isFlavourOptionMatch(opt, version, portions, packageHandle, packageId);

            opt.classList.toggle('is-hidden', !match);
        });
        updateFlavourBorders();
        updatePurchaseTypeAvailability();
        recalcTotalPrice();
        syncSubscriptionSelect();
    }

    function getCurrentProductOption(options) {
      if (!currentProductHandle) return null;

      return options.find(option =>
        option.dataset.productHandle === currentProductHandle &&
        option.dataset.available !== 'false' &&
        !option.classList.contains('is-disabled')
      ) || null;
    }

    function syncProductUrlForSelection(version, portions, packageHandle = '', packageId = '') {
      if (!version || !portions || !window.history || typeof window.history.replaceState !== 'function') {
        return;
      }

      const matchingOptions = Array.from(document.querySelectorAll('.flavour-option'))
        .filter(option =>
          !option.classList.contains('is-hidden') &&
          isFlavourOptionMatch(option, version, portions, packageHandle, packageId)
        );

      if (!matchingOptions.length) return;

      const selectedOption = matchingOptions.find(option => {
        const qty = parseInt(option.querySelector('.quantity-input')?.value || '0', 10);
        return qty > 0;
      });

      const firstAvailableOption = matchingOptions.find(option =>
        option.dataset.available !== 'false' && !option.classList.contains('is-disabled')
      );

      const currentUrlOption = getCurrentProductOption(matchingOptions);
      const targetOption = selectedOption || currentUrlOption || firstAvailableOption || matchingOptions[0];
      const targetHandle = String(targetOption?.dataset?.productHandle || '').trim();
      if (!targetHandle) return;

      const nextPath = `${getShopPathPrefix()}/products/${targetHandle}`;
      const currentPath = window.location.pathname;
      const currentSuffix = `${window.location.search}${window.location.hash}`;
      const nextUrl = `${nextPath}${currentSuffix}`;

      if (currentPath !== nextPath) {
        window.history.replaceState(window.history.state, '', nextUrl);
      }
    }

    // Automatic image refresh when product is added = '+' is clicked
    function setGalleryByFlavour(flavourOption) {
        gDebug('setGalleryByFlavour called', flavourOption?.dataset?.productImages);
        if (!flavourOption) return;

        const version = flavourOption.dataset.version;
        const portions = flavourOption.dataset.portions;
        const packageHandle = flavourOption.dataset.packageHandle || '';
        const packageId = flavourOption.dataset.packageId || '';

        // použij kliknutou příchuť jako zdroj pro galerii
        updateProductGallery(version, portions, packageHandle, packageId, flavourOption);
    }

    function resetAllSelectorQuantities() {
      document.querySelectorAll('.quantity-input').forEach(input => {
        if (parseInt(input.value || '0', 10) === 0) return;
        input.value = 0;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    function isGalleryZoomEnabled() {
      return true;
    }

    function getGalleryZoomElements() {
      const root = document.querySelector('[data-product-gallery-zoom]');
      if (!root) return null;
      if (!root.dataset.portalMounted && document.body && root.parentNode !== document.body) {
        document.body.appendChild(root);
        root.dataset.portalMounted = 'true';
      }
      const track = root.querySelector('[data-product-gallery-zoom-track]');
      let video = root.querySelector('[data-product-gallery-zoom-video]');
      if (!video && track) {
        video = document.createElement('video');
        video.className = 'product-gallery-zoom__video is-hidden';
        video.autoplay = true;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('data-product-gallery-zoom-video', '');
        track.appendChild(video);
      }
      return {
        root,
        image: root.querySelector('[data-product-gallery-zoom-image]'),
        ghostImage: root.querySelector('[data-product-gallery-zoom-image-ghost]'),
        stage: root.querySelector('[data-product-gallery-zoom-stage]'),
        track,
        video,
        dots: root.querySelector('[data-product-gallery-zoom-dots]'),
        prev: root.querySelector('[data-product-gallery-zoom-prev]'),
        next: root.querySelector('[data-product-gallery-zoom-next]'),
        closeTargets: Array.from(root.querySelectorAll('[data-product-gallery-zoom-close]'))
      };
    }

    function closeGalleryZoom() {
      const els = getGalleryZoomElements();
      if (!els) return;
      els.root.classList.remove('is-open');
      els.root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('product-gallery-zoom-open');
      document.body.classList.remove('product-gallery-zoom-open');
      if (els.video) {
        els.video.pause?.();
        els.video.classList.add('is-hidden');
        els.video.classList.remove('is-active');
        els.video.style.display = 'none';
      }
    }

    function syncGalleryZoom(track) {
      const els = getGalleryZoomElements();
      const activeImages = Array.isArray(track?._currentImages) ? track._currentImages : [];
      const activeVideo = String(track?._currentVideo || '').trim();
      if (!els || !els.image || !els.ghostImage || !els.track) return;

      if (activeVideo && els.video) {
        els.track.classList.remove('is-sliding');
        els.track.style.transform = 'translateX(0)';
        els.image.style.display = 'none';
        els.image.classList.add('is-hidden');
        els.image.classList.remove('is-active');
        els.ghostImage.style.display = 'none';
        els.ghostImage.classList.add('is-hidden');
        els.ghostImage.classList.remove('is-active');
        els.ghostImage.src = '';
        els.video.style.display = '';
        els.video.classList.add('is-active');
        els.video.classList.remove('is-hidden');
        if (els.video.src !== activeVideo) {
          els.video.src = activeVideo;
          els.video.load();
        }
        els.video.play?.().catch(() => {});
        els.root.dataset.index = '0';
        els.root.dataset.pendingDirection = '0';
        if (els.dots) {
          els.dots.innerHTML = '';
          els.dots.style.display = 'none';
        }
        if (els.prev) els.prev.style.display = 'none';
        if (els.next) els.next.style.display = 'none';
        return;
      }

      if (els.video) {
        els.video.pause?.();
        els.video.classList.add('is-hidden');
        els.video.classList.remove('is-active');
        els.video.style.display = 'none';
      }
      els.image.style.display = '';
      els.ghostImage.style.display = '';

      const currentIndex = typeof track?.currentIndex === 'number' ? track.currentIndex : 0;
      const activeSrc = activeImages[currentIndex] || '';
      const previousIndex = parseInt(els.root.dataset.index || '-1', 10);
      const pendingDirection = parseInt(els.root.dataset.pendingDirection || '0', 10) || 0;
      const previousSrc = els.image.currentSrc || els.image.src || '';
      const shouldSlide =
        els.root.classList.contains('is-open') &&
        previousIndex >= 0 &&
        pendingDirection !== 0 &&
        previousSrc &&
        activeSrc &&
        previousSrc !== activeSrc;

      if (!shouldSlide) {
        els.track.classList.remove('is-sliding');
        els.track.style.transform = 'translateX(0)';
        els.image.src = activeSrc;
        els.image.classList.add('is-active');
        els.image.classList.remove('gallery-no-transition', 'is-hidden');
        els.image.style.zIndex = '';
        els.ghostImage.classList.add('is-hidden');
        els.ghostImage.src = '';
        els.ghostImage.classList.remove('is-active', 'gallery-no-transition');
        els.ghostImage.style.zIndex = '';
      } else {
        const isForward = pendingDirection > 0;

        els.track.classList.remove('is-sliding');
        els.track.style.transform = isForward ? 'translateX(0)' : 'translateX(-100%)';

        if (isForward) {
          els.image.src = previousSrc;
          els.ghostImage.src = activeSrc;
        } else {
          els.image.src = activeSrc;
          els.ghostImage.src = previousSrc;
        }

        els.image.classList.remove('gallery-no-transition', 'is-hidden');
        els.ghostImage.classList.remove('gallery-no-transition', 'is-hidden');
        els.image.classList.add('is-active');
        els.ghostImage.classList.add('is-active');

        void els.track.offsetWidth;

        requestAnimationFrame(() => {
          els.track.classList.add('is-sliding');
          els.track.style.transform = isForward ? 'translateX(-100%)' : 'translateX(0)';
        });

        const finishZoomSlide = (event) => {
          if (event && event.target !== els.track) return;
          els.track.removeEventListener('transitionend', finishZoomSlide);
          els.track.classList.remove('is-sliding');
          els.track.style.transform = 'translateX(0)';
          els.image.src = activeSrc;
          els.image.classList.add('is-active');
          els.image.classList.remove('gallery-no-transition', 'is-hidden');
          els.ghostImage.classList.add('gallery-no-transition', 'is-hidden');
          els.ghostImage.classList.remove('is-active');
          els.ghostImage.src = '';
          requestAnimationFrame(() => {
            els.ghostImage.classList.remove('gallery-no-transition');
          });
        };

        els.track.addEventListener('transitionend', finishZoomSlide);
      }

      els.root.dataset.index = String(currentIndex);
      els.root.dataset.pendingDirection = '0';

      if (els.dots) {
        els.dots.innerHTML = '';
        activeImages.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = i === currentIndex ? 'is-active' : '';
          dot.addEventListener('click', () => {
            if (!track || typeof track._setActive !== 'function') return;
            const direction = i === track.currentIndex ? 0 : (i > track.currentIndex ? 1 : -1);
            els.root.dataset.pendingDirection = String(direction);
            track._setActive(i, direction);
          });
          els.dots.appendChild(dot);
        });
        els.dots.style.display = activeImages.length > 1 ? '' : 'none';
      }

      if (els.prev) els.prev.style.display = activeImages.length > 1 ? '' : 'none';
      if (els.next) els.next.style.display = activeImages.length > 1 ? '' : 'none';
    }

    function openGalleryZoom(track) {
      if (!isGalleryZoomEnabled() || !track) return;
      const els = getGalleryZoomElements();
      if (!els || !els.image) return;
      els.root.dataset.pendingDirection = '0';
      els.root.dataset.index = String(typeof track.currentIndex === 'number' ? track.currentIndex : 0);
      syncGalleryZoom(track);
      els.root.classList.add('is-open');
      els.root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-gallery-zoom-open');
      document.body.classList.add('product-gallery-zoom-open');
    }

    function bindGalleryZoom(gallery, track) {
      if (!gallery || gallery.dataset.zoomBound === 'true') return;
      gallery.dataset.zoomBound = 'true';
      const els = getGalleryZoomElements();
      if (els) {
        els.closeTargets.forEach((target) => target.addEventListener('click', closeGalleryZoom));
        if (els.prev) {
          els.prev.addEventListener('click', () => {
            if (!track || typeof track._setActive !== 'function' || !Array.isArray(track._currentImages) || track._currentImages.length <= 1) return;
            const nextIndex = (track.currentIndex - 1 + track._currentImages.length) % track._currentImages.length;
            els.root.dataset.pendingDirection = '-1';
            track._setActive(nextIndex, -1);
          });
        }
        if (els.next) {
          els.next.addEventListener('click', () => {
            if (!track || typeof track._setActive !== 'function' || !Array.isArray(track._currentImages) || track._currentImages.length <= 1) return;
            const nextIndex = (track.currentIndex + 1) % track._currentImages.length;
            els.root.dataset.pendingDirection = '1';
            track._setActive(nextIndex, 1);
          });
        }
        if (els.stage) {
          let zoomTouchStartX = 0;
          let zoomTouchStartY = 0;
          let zoomSwipeLockedHorizontal = false;
          let zoomWheelLockedUntil = 0;

          const zoomGoDirection = (direction) => {
            if (!track || typeof track._setActive !== 'function' || !Array.isArray(track._currentImages) || track._currentImages.length <= 1) return;
            const nextIndex = direction > 0
              ? (track.currentIndex + 1) % track._currentImages.length
              : (track.currentIndex - 1 + track._currentImages.length) % track._currentImages.length;
            els.root.dataset.pendingDirection = String(direction > 0 ? 1 : -1);
            track._setActive(nextIndex, direction > 0 ? 1 : -1);
          };

          els.stage.addEventListener('touchstart', (e) => {
            if (!e.touches || !e.touches.length) return;
            zoomTouchStartX = e.touches[0].clientX;
            zoomTouchStartY = e.touches[0].clientY;
            zoomSwipeLockedHorizontal = false;
          }, { passive: true });
          els.stage.addEventListener('touchmove', (e) => {
            if (!e.touches || !e.touches.length) return;
            const dx = e.touches[0].clientX - zoomTouchStartX;
            const dy = e.touches[0].clientY - zoomTouchStartY;
            if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
              zoomSwipeLockedHorizontal = true;
              if (e.cancelable) e.preventDefault();
            }
          }, { passive: false });
          els.stage.addEventListener('touchend', (e) => {
            if (!e.changedTouches || !e.changedTouches.length) return;
            const dx = e.changedTouches[0].clientX - zoomTouchStartX;
            const dy = e.changedTouches[0].clientY - zoomTouchStartY;
            if (!zoomSwipeLockedHorizontal && (Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy))) return;
            zoomGoDirection(dx < 0 ? 1 : -1);
          }, { passive: true });
          els.stage.addEventListener('wheel', (e) => {
            const now = Date.now();
            if (now < zoomWheelLockedUntil) return;
            const absX = Math.abs(e.deltaX || 0);
            const absY = Math.abs(e.deltaY || 0);
            if (absX < 14 || absX <= absY) return;
            if (e.cancelable) e.preventDefault();
            zoomWheelLockedUntil = now + 420;
            zoomGoDirection(e.deltaX > 0 ? 1 : -1);
          }, { passive: false });
        }
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') closeGalleryZoom();
        });
      }

      let tapStartX = 0;
      let tapStartY = 0;
      let tapMoved = false;

      gallery.addEventListener('touchstart', (e) => {
        if (!isGalleryZoomEnabled()) return;
        if (e.target.closest('.gallery-arrow') || e.target.closest('.gallery-dots')) return;
        if (!e.touches || !e.touches.length) return;
        tapStartX = e.touches[0].clientX;
        tapStartY = e.touches[0].clientY;
        tapMoved = false;
      }, { passive: true });

      gallery.addEventListener('touchmove', (e) => {
        if (!isGalleryZoomEnabled()) return;
        if (!e.touches || !e.touches.length) return;
        const dx = e.touches[0].clientX - tapStartX;
        const dy = e.touches[0].clientY - tapStartY;
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          tapMoved = true;
        }
      }, { passive: true });

      gallery.addEventListener('touchend', (e) => {
        if (!isGalleryZoomEnabled()) return;
        if (e.target.closest('.gallery-arrow') || e.target.closest('.gallery-dots')) return;
        if (tapMoved) {
          gallery.dataset.suppressZoomClick = 'true';
          setTimeout(() => {
            gallery.dataset.suppressZoomClick = 'false';
          }, 350);
          return;
        }
        const activeVideo = track ? track.querySelector('video.is-active:not(.is-hidden)') : null;
        const videoSrc = activeVideo ? (track._currentVideo || activeVideo.currentSrc || activeVideo.src || '') : '';
        const activeImg = track ? track.querySelector('img.is-active:not(.is-hidden)') : null;
        const src = videoSrc || activeImg?.currentSrc || activeImg?.src || '';
        if (!src) return;
        gallery.dataset.suppressZoomClick = 'true';
        setTimeout(() => {
          gallery.dataset.suppressZoomClick = 'false';
        }, 350);
        openGalleryZoom(track);
      }, { passive: true });

      gallery.addEventListener('click', (e) => {
        if (!isGalleryZoomEnabled()) return;
        if (gallery.dataset.suppressZoomClick === 'true') return;
        if (e.target.closest('.gallery-arrow') || e.target.closest('.gallery-dots')) return;
        const activeVideo = track ? track.querySelector('video.is-active:not(.is-hidden)') : null;
        const videoSrc = activeVideo ? (track._currentVideo || activeVideo.currentSrc || activeVideo.src || '') : '';
        const activeImg = track ? track.querySelector('img.is-active:not(.is-hidden)') : null;
        const src = videoSrc || activeImg?.currentSrc || activeImg?.src || '';
        if (!src) return;
        openGalleryZoom(track);
      });
    }

    function autoAddFlavourForSelection(version, portions, packageHandle = '', packageId = '') {
      if (!autoAddProductEnabled || !version || !portions) return null;

      const visibleOptions = Array.from(document.querySelectorAll('.flavour-option'))
        .filter(option =>
          !option.classList.contains('is-hidden') &&
          isFlavourOptionMatch(option, version, portions, packageHandle, packageId) &&
          option.dataset.available !== 'false' &&
          !option.classList.contains('is-disabled')
        );

      if (!visibleOptions.length) return null;

      let targetOption = null;
      if (currentProductHandle) {
        targetOption = visibleOptions.find(option => option.dataset.productHandle === currentProductHandle) || null;
      }
      if (!targetOption) {
        targetOption = visibleOptions[0];
      }

      const qtyInput = targetOption.querySelector('.quantity-input');
      if (!qtyInput) return null;
      const qtySelector = targetOption.querySelector('.quantity-selector');

      if (qtySelector) {
        qtySelector.classList.add('no-qty-anim');
      }

      qtyInput.value = 1;
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));

      if (qtySelector) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            qtySelector.classList.remove('no-qty-anim');
          });
        });
      }
      return targetOption;
    }

    function updateProductGallery(version, portions, packageHandle = '', packageId = '', sourceOption) {
        const gallery = document.querySelector('[data-product-gallery]');
        if (!gallery) return;

        const track = gallery.querySelector('.product-gallery-track');
        if (!track) return;
        bindGalleryZoom(gallery, track);
        const dotsContainer = gallery.querySelector('[data-gallery-dots]');
        const arrowPrev = gallery.querySelector('.gallery-arrow.prev');
        const arrowNext = gallery.querySelector('.gallery-arrow.next');

        const matchingOptions = Array.from(document.querySelectorAll('.flavour-option:not(.is-hidden)'))
          .filter(option => isFlavourOptionMatch(option, version, portions, packageHandle, packageId));
        const firstVisible = sourceOption || getCurrentProductOption(matchingOptions) || matchingOptions[0];

        if (!firstVisible) {
            destroyVideoGalleryPin();
            closeGalleryZoom();
            if (dotsContainer) dotsContainer.innerHTML = '';
            if (dotsContainer) dotsContainer.style.display = 'none';
            if (arrowPrev) arrowPrev.style.display = 'none';
            if (arrowNext) arrowNext.style.display = 'none';
            // Hide all images
            if (track._images) {
                track._images.forEach(img => {
                    img.classList.add('is-hidden');
                    img.classList.remove('is-active');
                });
            } else {
                track.innerHTML = '';
            }
            return;
        }

        let images = [];
        try {
            images = JSON.parse(firstVisible.dataset.productImages || '[]');
        } catch {
            images = [];
        }
        const videoSrc = String(firstVisible.dataset.productVideo || '').trim();
        const videoPoster = String(firstVisible.dataset.productVideoPoster || images[0] || '').trim();
        gDebug('updateProductGallery media', { videoSrc, videoPoster, images });

        if (!videoSrc && !images.length) {
            destroyVideoGalleryPin();
            closeGalleryZoom();
            if (dotsContainer) dotsContainer.innerHTML = '';
            if (dotsContainer) dotsContainer.style.display = 'none';
            if (arrowPrev) arrowPrev.style.display = 'none';
            if (arrowNext) arrowNext.style.display = 'none';
            // Hide all images
            if (track._images) {
                track._images.forEach(img => {
                    img.classList.add('is-hidden');
                    img.classList.remove('is-active');
                });
            } else {
                track.innerHTML = '';
            }
            return;
        }

        // === PERSISTENT GALLERY ENGINE ===
        if (!track._images) {
            track._images = new Map(); // url -> img element
            track.currentIndex = 0;
        }
        track._currentImages = images.slice();

        if (videoSrc) {
            closeGalleryZoom();

            if (!track._video) {
                const video = document.createElement('video');
                video.className = 'product-gallery-video';
                video.autoplay = true;
                video.muted = true;
                video.loop = true;
                video.preload = 'auto';
                video.playsInline = true;
                video.setAttribute('playsinline', '');
                video.setAttribute('muted', '');
                video.setAttribute('preload', 'auto');
                track._video = video;
                track.appendChild(video);
            }

            if (videoPoster && track._video.getAttribute('poster') !== videoPoster) {
                track._video.poster = videoPoster;
            }

            track._images.forEach(img => img.classList.remove('is-default-media'));
            track._video.classList.add('is-default-media');
            track._video.classList.add('is-active');
            track._video.classList.remove('is-hidden');

            if (track._video.getAttribute('src') !== videoSrc) {
                track._video.src = videoSrc;
                track._video.load();
            }

            track._video.play?.().catch(() => {});
            track.currentIndex = 0;
            track._currentVideo = videoSrc;
            createVideoGalleryPin();

            track._images.forEach(img => {
                img.classList.add('is-hidden');
                img.classList.remove('is-active');
            });

            if (dotsContainer) {
                dotsContainer.innerHTML = '';
                dotsContainer.style.display = 'none';
            }
            if (arrowPrev) arrowPrev.style.display = 'none';
            if (arrowNext) arrowNext.style.display = 'none';
            return;
        }

        destroyVideoGalleryPin();
        track._currentVideo = '';
        if (track._video) {
            track._video.pause?.();
            track._video.classList.add('is-hidden');
            track._video.classList.remove('is-active');
            track._video.classList.remove('is-default-media');
        }

        // ensure all images exist (or are created once)
        images.forEach((src, i) => {
            if (!track._images.has(src)) {
                gDebug('create img', src);
                const img = document.createElement('img');
                img.src = src;
                img.dataset.index = i;
                track._images.set(src, img);
                track.appendChild(img);

                // preload tracking
                if (!galleryImageCache.has(src)) {
                    img.onload = () => galleryImageCache.set(src, true);
                }
            }
        });

        // keep current active visible until new image is ready (avoid flash)
        const currentActive = Array.from(track._images.values())
          .find(img => img.classList.contains('is-active'));

        track._images.forEach((img, src) => {
          if (img === currentActive) {
            img.classList.remove('is-hidden');
            return;
          }
          img.classList.toggle('is-hidden', !images.includes(src));
        });

        // activate first image only after it's loaded to avoid flash
        const firstSrc = images[0];
        const firstImg = track._images.get(firstSrc);
        track._images.forEach(img => img.classList.remove('is-default-media'));
        if (firstImg) {
            firstImg.classList.add('is-default-media');
        }
        if (firstImg) {
            const currentActive = Array.from(track._images.values())
              .find(img => img.classList.contains('is-active'));

            const activate = () => {
              gDebug('gallery activate', {
                firstSrc,
                currentActive: currentActive ? currentActive.src : null,
                target: firstImg.src,
                complete: firstImg.complete,
                naturalWidth: firstImg.naturalWidth
              });
              // Add new active first (allow crossfade)
              firstImg.classList.add('is-active');
              // Remove previous active on next frame to avoid flash
              if (currentActive && currentActive !== firstImg) {
                requestAnimationFrame(() => {
                  currentActive.classList.remove('is-active');
                });
              }
              track.currentIndex = 0;

              // keep current set visible; hide only images not in the set
              track._images.forEach((img, src) => {
                img.classList.toggle('is-hidden', !images.includes(src));
              });
            };

            if (firstImg.complete && firstImg.naturalWidth > 0) {
              gDebug('gallery activate immediate', { firstSrc });
              activate();
            } else {
              gDebug('gallery wait onload', { firstSrc });
              firstImg.onload = activate;
            }
        }

        // dots (no waiting)
        if (dotsContainer) {
            dotsContainer.innerHTML = '';
            images.forEach((_, i) => {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = i === 0 ? 'is-active' : '';
                dot.addEventListener('click', () => {
                  const current = typeof track.currentIndex === 'number' ? track.currentIndex : 0;
                  const direction = i === current ? 0 : (i > current ? 1 : -1);
                  setActive(i, direction);
                });
                dotsContainer.appendChild(dot);
            });
            dotsContainer.style.display = images.length > 1 ? '' : 'none';
        }

        // arrows always available if >1 image
        if (arrowPrev) arrowPrev.style.display = images.length > 1 ? '' : 'none';
        if (arrowNext) arrowNext.style.display = images.length > 1 ? '' : 'none';

        track._isSliding = false;
        track._queuedSetActive = null;
        clearTimeout(track._slideSafetyTimer);

        const flushQueuedSetActive = () => {
          const queued = track._queuedSetActive;
          if (!queued) return;
          track._queuedSetActive = null;
          setActive(queued.index, queued.direction);
        };

        function setActive(index, direction = 0) {
            gDebug('setActive called', {
              index,
              images,
              currentIndex: track.currentIndex,
              direction
            });
            const visibleImgs = images.map(src => track._images.get(src)).filter(Boolean);
            const img = visibleImgs[index];
            if (!img) return;
            const current = visibleImgs[track.currentIndex];

            if (track._isSliding) {
              track._queuedSetActive = { index, direction };
              return;
            }

            // 🔥 bez tohohle se nové slide nikdy nenačtou
            if (!img.src || img.src !== images[index]) {
                img.src = images[index];
            }
            gDebug('setActive img state', {
              src: img.src,
              complete: img.complete,
              naturalWidth: img.naturalWidth
            });

            visibleImgs.forEach((imageEl) => {
              imageEl.classList.remove(
                'gallery-slide-pre-right',
                'gallery-slide-pre-left',
                'gallery-slide-out-right',
                'gallery-slide-out-left'
              );
            });

            if (!direction || !current || current === img) {
              track.classList.remove('is-sliding');
              track.style.removeProperty('--gallery-slide-distance');
              visibleImgs.forEach((i, idx) =>
                i.classList.toggle('is-active', idx === index)
              );
              clearTimeout(track._slideSafetyTimer);
              track._isSliding = false;
            } else {
              const preClass = direction > 0 ? 'gallery-slide-pre-right' : 'gallery-slide-pre-left';
              const outClass = direction > 0 ? 'gallery-slide-out-left' : 'gallery-slide-out-right';
              const trackWidth = track.getBoundingClientRect().width || 0;
              const currentWidth = current.getBoundingClientRect().width || 0;
              const nextWidth = img.getBoundingClientRect().width || 0;
              const slideDistance = Math.ceil((trackWidth + Math.max(currentWidth, nextWidth)) / 2);

              track._isSliding = true;
              track.classList.add('is-sliding');
              track.style.setProperty('--gallery-slide-distance', `${slideDistance}px`);
              img.classList.remove('is-hidden');
              img.classList.add('gallery-no-transition');
              img.classList.add(preClass);
              img.classList.add('is-active');

              // Place the next image off-canvas without animation, then animate only the slide-in.
              void img.offsetWidth;
              requestAnimationFrame(() => {
                img.classList.remove('gallery-no-transition');
                img.classList.remove(preClass);
              });

              current.classList.remove('is-hidden');
              current.classList.add(outClass);
              let cleanedUp = false;
              const clearCurrent = (event) => {
                if (cleanedUp) return;
                if (event && event.target !== current) return;
                cleanedUp = true;
                current.classList.add('gallery-no-transition');
                current.classList.remove('is-active', outClass);
                current.classList.add('is-hidden');
                img.classList.remove('gallery-no-transition');
                current.removeEventListener('transitionend', clearCurrent);
                clearTimeout(track._slideSafetyTimer);
                track.classList.remove('is-sliding');
                track.style.removeProperty('--gallery-slide-distance');
                track._isSliding = false;
                visibleImgs.forEach((imageEl, imageIndex) => {
                  imageEl.classList.toggle('is-hidden', imageIndex !== index);
                });
                requestAnimationFrame(() => {
                  current.classList.remove('gallery-no-transition');
                });
                flushQueuedSetActive();
              };
              current.addEventListener('transitionend', clearCurrent);
              track._slideSafetyTimer = setTimeout(() => {
                clearCurrent();
              }, 420);
            }

            if (dotsContainer) {
                Array.from(dotsContainer.children).forEach((dot, i) =>
                    dot.classList.toggle('is-active', i === index)
                );
            }

            track.currentIndex = index;
            if (document.querySelector('[data-product-gallery-zoom]')?.classList.contains('is-open')) {
              syncGalleryZoom(track);
            }
        }
        track._setActive = setActive;

        if (arrowPrev && arrowNext) {
            arrowPrev.onclick = () => {
                const visibleImgs = images
                    .map(src => track._images.get(src))
                    .filter(Boolean);

                if (!visibleImgs.length) return;

                const nextIndex =
                    (track.currentIndex - 1 + visibleImgs.length) % visibleImgs.length;

                setActive(nextIndex, -1);
            };

            arrowNext.onclick = () => {
                const visibleImgs = images
                    .map(src => track._images.get(src))
                    .filter(Boolean);

                if (!visibleImgs.length) return;

                const nextIndex =
                    (track.currentIndex + 1) % visibleImgs.length;

                setActive(nextIndex, 1);
            };
        }

        // Mobile swipe / trackpad horizontal scroll support (bind once, use latest image set via track._currentImages)
        if (!track._swipeBound) {
            track._swipeBound = true;
            const swipeSurface = gallery || track;
            let touchStartX = 0;
            let touchStartY = 0;
            let swipeLockedHorizontal = false;
            let wheelLockedUntil = 0;

            const markSwipeGesture = () => {
              track._lastGestureWasSwipe = true;
              clearTimeout(track._lastGestureSwipeTimer);
              track._lastGestureSwipeTimer = setTimeout(() => {
                track._lastGestureWasSwipe = false;
              }, 260);
            };

            const goGalleryDirection = (direction) => {
              const activeImages = track._currentImages || [];
              if (activeImages.length <= 1 || typeof track._setActive !== 'function') return;
              markSwipeGesture();
              const nextIndex = direction > 0
                ? (track.currentIndex + 1) % activeImages.length
                : (track.currentIndex - 1 + activeImages.length) % activeImages.length;
              track._setActive(nextIndex, direction > 0 ? 1 : -1);
            };

            swipeSurface.addEventListener('touchstart', (e) => {
                if (!e.touches || !e.touches.length) return;
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                swipeLockedHorizontal = false;
            }, { passive: true });

            swipeSurface.addEventListener('touchmove', (e) => {
                if (!e.touches || !e.touches.length) return;
                const dx = e.touches[0].clientX - touchStartX;
                const dy = e.touches[0].clientY - touchStartY;
                if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
                    swipeLockedHorizontal = true;
                }
            }, { passive: true });

            swipeSurface.addEventListener('touchend', (e) => {
                if (!e.changedTouches || !e.changedTouches.length) return;
                const touchEndX = e.changedTouches[0].clientX;
                const touchEndY = e.changedTouches[0].clientY;
                const dx = touchEndX - touchStartX;
                const dy = touchEndY - touchStartY;

                // React only to intentional horizontal swipe.
                if (!swipeLockedHorizontal && (Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy))) return;
                goGalleryDirection(dx < 0 ? 1 : -1);
            }, { passive: true });

            swipeSurface.addEventListener('wheel', (e) => {
              const now = Date.now();
              if (now < wheelLockedUntil) return;
              const absX = Math.abs(e.deltaX || 0);
              const absY = Math.abs(e.deltaY || 0);
              if (absX < 14 || absX <= absY) return;
              if (e.cancelable) e.preventDefault();
              wheelLockedUntil = now + 420;
              goGalleryDirection(e.deltaX > 0 ? 1 : -1);
            }, { passive: false });
        }

    }

    function getPreferredPortionsForVersion(version) {
      if (!currentProductHandle || !version) return null;

      const matchingOption = Array.from(flavourOptions).find(option =>
        option.dataset.version === String(version) &&
        option.dataset.productHandle === currentProductHandle &&
        option.dataset.available !== 'false' &&
        !option.classList.contains('is-disabled')
      );

      return matchingOption?.dataset?.portions || null;
    }

    function getPreferredPackageHandleForVersion(version) {
      if (!currentProductHandle || !version) return '';

      const matchingOption = Array.from(flavourOptions).find(option =>
        option.dataset.version === String(version) &&
        option.dataset.productHandle === currentProductHandle &&
        option.dataset.available !== 'false' &&
        !option.classList.contains('is-disabled')
      );

      return matchingOption?.dataset?.packageHandle || '';
    }

    function getPreferredPackageIdForVersion(version) {
      if (!currentProductHandle || !version) return '';

      const matchingOption = Array.from(flavourOptions).find(option =>
        option.dataset.version === String(version) &&
        option.dataset.productHandle === currentProductHandle &&
        option.dataset.available !== 'false' &&
        !option.classList.contains('is-disabled')
      );

      return matchingOption?.dataset?.packageId || '';
    }

    function syncNutritionPopupByVersion(activeVersion) {
      // Outside buy-section context keep original nutrition-popup behavior (URL based, etc.)
      if (!buySectionRoot || !document.querySelector('.category-product[data-version]')) return;

      const activePortions = document.querySelector('.package-product[aria-pressed="true"]')?.dataset?.portions || '';
      const activePackageHandle = document.querySelector('.package-product[aria-pressed="true"]')?.dataset?.packageHandle || '';
      const activePackageId = document.querySelector('.package-product[aria-pressed="true"]')?.dataset?.packageId || '';
      const byVersion = Array.from(document.querySelectorAll('.flavour-option'))
        .filter(option =>
          !option.classList.contains('is-hidden') &&
          (!activeVersion || option.dataset.version === String(activeVersion)) &&
          (!activePortions || isFlavourOptionMatch(option, activeVersion || option.dataset.version, activePortions, activePackageHandle, activePackageId))
        );
      const selected = byVersion.find(option => {
        const qty = parseInt(option.querySelector('.quantity-input')?.value || '0', 10);
        return qty > 0;
      });
      const sourceOption = selected || getCurrentProductOption(byVersion) || byVersion[0] || null;
      const targetHandle = sourceOption?.dataset?.productHandle || '';

      // Pull nutrition blocks for selected buy-section product without relying on URL handle.
      if (targetHandle && targetHandle !== lastNutritionSyncHandle) {
        ensureNutritionForHandle(targetHandle, { apply: true }).catch((error) => {
          gDebug('nutrition sync failed', { targetHandle, error });
        });
      }

      const labels = Array.from(document.querySelectorAll('#nutrition_information_buy_popup .labels-container[data-label-tags]'));
      if (!labels.length) return;

      if (!buySectionUsesVersionTags) {
        labels.forEach(label => {
          label.style.display = '';
        });
        return;
      }

      const versionKey = String(activeVersion || '').toLowerCase().trim();
      const hasVersionFilter = !!versionKey;
      let visibleCount = 0;

      labels.forEach(label => {
        if (!hasVersionFilter) {
          label.style.display = '';
          return;
        }
        const tagsRaw = String(label.dataset.labelTags || '').toLowerCase();
        const tags = tagsRaw ? tagsRaw.split('|').map(t => t.trim()).filter(Boolean) : [];
        const match = tags.includes(versionKey);
        label.style.display = match ? '' : 'none';
        if (match) visibleCount += 1;
      });

      // Safety fallback: if nothing matches selected version, do not hide everything.
      if (hasVersionFilter && visibleCount === 0) {
        labels.forEach(label => {
          label.style.display = '';
        });
      }
    }

    function activateVersion(version) {
        const packageSelectorEl = document.querySelector('.package-selector');
        versionButtons.forEach(btn => {
            btn.setAttribute('aria-pressed', btn.dataset.version === version ? 'true' : 'false');
        });

        let firstVisible = null;

        packageButtons.forEach(btn => {
            if (btn.dataset.version === version) {
                btn.style.display = '';
                if (!firstVisible) firstVisible = btn;
            } else {
                btn.style.display = 'none';
                btn.setAttribute('aria-pressed', 'false');
            }
        });

        const preferredPortions = getPreferredPortionsForVersion(version);
        const preferredPackageHandle = getPreferredPackageHandleForVersion(version);
        const preferredPackageId = getPreferredPackageIdForVersion(version);
        if (preferredPortions) {
          const preferredButton = Array.from(packageButtons).find(btn =>
            btn.style.display !== 'none' &&
            btn.dataset.version === String(version) &&
            btn.dataset.portions === String(preferredPortions) &&
            (
              (!preferredPackageId || normalizePackageId(btn.dataset.packageId) === normalizePackageId(preferredPackageId)) &&
              (!preferredPackageHandle || normalizePackageHandle(btn.dataset.packageHandle) === normalizePackageHandle(preferredPackageHandle))
            )
          );
          if (preferredButton) {
            firstVisible = preferredButton;
          }
        }

        const visiblePackageCount = Array.from(packageButtons)
          .filter(btn => btn.style.display !== 'none').length;
        if (packageSelectorEl) {
          packageSelectorEl.classList.toggle('is-single-size', visiblePackageCount <= 1);
        }

        let activePortions = null;
        let activePackageHandle = '';
        let activePackageId = '';
        if (firstVisible) {
            firstVisible.setAttribute('aria-pressed', 'true');
            activePortions = firstVisible.dataset.portions || null;
            activePackageHandle = firstVisible.dataset.packageHandle || '';
            activePackageId = firstVisible.dataset.packageId || '';
        }

        const versionInfoBlocks = document.querySelectorAll('.version-info');
        versionInfoBlocks.forEach(block => {
            block.style.display = block.dataset.version === version ? 'block' : 'none';
        });

        resetAllSelectorQuantities();
        updateFlavours(version, activePortions, activePackageHandle, activePackageId);
        syncSubscriptionSelect();
        // prefetch images for this version ASAP
        preloadGalleryUrls(
          collectGalleryImageUrls(opt => opt.dataset.version === String(version))
        );
        const autoAddedOption = autoAddFlavourForSelection(version, activePortions, activePackageHandle, activePackageId);
        if (autoAddedOption) {
          setGalleryByFlavour(autoAddedOption);
        } else {
          updateProductGallery(version, activePortions, activePackageHandle, activePackageId);
        }
        animateFeatures(version);
        // ensure gluten state is computed from the now-visible flavour list
        updateGlutenFree(version, activePortions, activePackageHandle, activePackageId);
        syncNutritionPopupByVersion(version);
        prefetchNutritionForSelection(version, activePortions);
        syncProductUrlForSelection(version, activePortions, activePackageHandle, activePackageId);
        recalcTotalPrice();
        updatePricePerPortion();
    }

    const initialVersionFromProduct = document
      .querySelector('.buy-section')
      ?.dataset?.initialVersion;

    let initialBtn = null;

    if (initialVersionFromProduct) {
      initialBtn = Array.from(versionButtons)
        .find(b => b.dataset.version === initialVersionFromProduct) || null;
    }

    if (!initialBtn) {
      initialBtn =
        Array.from(versionButtons).find(b => b.getAttribute('aria-pressed') === 'true') ||
        versionButtons[0];
    }

    requestAnimationFrame(() => {
      if (initialBtn) {
        activateVersion(initialBtn.dataset.version);
      } else {
        syncNutritionPopupByVersion('');
      }
    });
    updateFlavourBorders();

    versionButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            triggerSelectionHaptic();
            activateVersion(btn.dataset.version);
        });
    });

    const nutritionLabelButton = document.getElementById('nutrition-package-popup-trigger');
    if (nutritionLabelButton) {
      nutritionLabelButton.addEventListener('click', () => {
        const activeVersion = document.querySelector('.category-product[aria-pressed="true"]')?.dataset?.version || '';
        syncNutritionPopupByVersion(activeVersion);
      });
    }

    packageButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            triggerSelectionHaptic();
            packageButtons.forEach(b => {
                if (b.dataset.version === btn.dataset.version) {
                    b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
                }
            });

            resetAllSelectorQuantities();
            updateFlavours(btn.dataset.version, btn.dataset.portions, btn.dataset.packageHandle || '', btn.dataset.packageId || '');
            syncSubscriptionSelect();
            const autoAddedOption = autoAddFlavourForSelection(btn.dataset.version, btn.dataset.portions, btn.dataset.packageHandle || '', btn.dataset.packageId || '');
            if (autoAddedOption) {
              setGalleryByFlavour(autoAddedOption);
            } else {
              updateProductGallery(btn.dataset.version, btn.dataset.portions, btn.dataset.packageHandle || '', btn.dataset.packageId || '');
            }
            updateGlutenFree(btn.dataset.version, btn.dataset.portions, btn.dataset.packageHandle || '', btn.dataset.packageId || '');
            prefetchNutritionForSelection(btn.dataset.version, btn.dataset.portions);
            syncProductUrlForSelection(btn.dataset.version, btn.dataset.portions, btn.dataset.packageHandle || '', btn.dataset.packageId || '');
            recalcTotalPrice();
            updatePricePerPortion();
        });
    });
    // Quantity selector +/- logic (collapsed when 0)
    const qtySelectors = document.querySelectorAll('.quantity-selector');

    qtySelectors.forEach(selector => {
        const input = selector.querySelector('.quantity-input');
        const plus = selector.querySelector('.plus');
        const minus = selector.querySelector('.minus');

        const updateQtyUI = () => {
            const value = parseInt(input.value || 0, 10);
            const flavourOption = selector.closest('.flavour-option');
            const highlightTag = flavourOption?.querySelector('.flavour-highlight-tag');
            selector.classList.toggle('is-empty', value === 0);
            if (highlightTag) {
                if (highlightTag._hideTimer) {
                    clearTimeout(highlightTag._hideTimer);
                    highlightTag._hideTimer = null;
                }

                if (value > 0) {
                    highlightTag.classList.add('is-fading');
                    highlightTag._hideTimer = setTimeout(() => {
                        if (parseInt(input.value || 0, 10) > 0) {
                            highlightTag.classList.add('is-hidden');
                            highlightTag.classList.remove('is-fading');
                        }
                    }, 220);
                } else if (highlightTag.classList.contains('is-hidden') || highlightTag.classList.contains('is-fading')) {
                    highlightTag.classList.remove('is-hidden');
                    highlightTag.classList.add('is-fading');
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            highlightTag.classList.remove('is-fading');
                        });
                    });
                }
            }
        };

        const updateDisabledState = () => {
            const isDisabled =
                selector.closest('.flavour-option')?.classList.contains('is-disabled');

            [plus, minus].forEach(btn => {
                if (!btn) return;
                if (isDisabled) {
                    btn.setAttribute('disabled', 'disabled');
                } else {
                    btn.removeAttribute('disabled');
                }
            });
        };

        // initial state
        updateQtyUI();
        updateDisabledState();

        plus.addEventListener('click', () => {
            if (selector.closest('.flavour-option')?.classList.contains('is-disabled')) {
                return;
            }

            triggerSelectionHaptic();
            input.value = parseInt(input.value || 0, 10) + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            updateQtyUI();
            updateDisabledState();
            // === switch gallery to this flavour on first add ===
            if (parseInt(input.value || '0', 10) === 1) {
                const flavourOption = selector.closest('.flavour-option');
                setGalleryByFlavour(flavourOption);
            }
        });


        minus.addEventListener('click', () => {
            const current = parseInt(input.value || 0, 10);
            if (current > 0) {
                triggerSelectionHaptic();
                input.value = current - 1;
                input.dispatchEvent(new Event('change', { bubbles: true }));
                updateQtyUI();
            }
        });

        input.addEventListener('input', updateQtyUI);
        input.addEventListener('change', updateQtyUI);
    });
    function hasAnyVisibleSubscription() {
        const hasSubscriptionCapability = (opt) => {
            if (!opt) return false;
            const subPrice = parseInt(opt.dataset.subscriptionPriceCents || '0', 10);
            if (!(subPrice > 0)) return false;
            if (opt.dataset.onetimeOnly === 'true') return false;
            if (opt.dataset.available === 'false') return false;
            return true;
        };

        return Array.from(document.querySelectorAll('.flavour-option[data-price-cents]'))
            .some(opt =>
                !opt.classList.contains('is-hidden') &&
                hasSubscriptionCapability(opt)
            );
    }

    function getUnitPrice(option) {
      const base = parseInt(option.dataset.priceCents || '0', 10);
      const sub  = parseInt(option.dataset.subscriptionPriceCents || '0', 10);

      if (purchaseTypeState.type === 'subscription' && sub > 0) {
        return sub;
      }
      return base;
    }

    const freeShippingThreshold = parseInt(
      buySectionRoot?.dataset?.freeShippingThreshold || '0',
      10
    );
    const buyFreeShippingBar = buySectionRoot?.querySelector('[data-buy-free-shipping]');
    const buyFreeShippingText = buySectionRoot?.querySelector('[data-buy-free-shipping-text]');
    const buyFreeShippingProgress = buySectionRoot?.querySelector('[data-buy-free-shipping-progress]');
    const buyFreeShippingIcon = buySectionRoot?.querySelector('[data-buy-free-shipping-icon]');
    const freeShippingEnabled = buySectionRoot?.dataset?.freeShippingEnabled === 'true';
    const subscriptionFreeDeliveryEnabled =
      buySectionRoot?.dataset?.subscriptionFreeDeliveryEnabled === 'true';
    const freeShippingReachedTemplate = buySectionRoot?.dataset?.freeShippingMessageReached || '';
    const freeShippingRemainingTemplate = buySectionRoot?.dataset?.freeShippingMessageRemaining || '[price]';
    let cartBaseTotalCents = 0;
    let cartHasSubscription = false;

    function getSelectedPendingTotals() {
      let selectedTotalCents = 0;
      let selectedHasSubscription = false;

      buySectionRoot?.querySelectorAll('.flavour-option[data-price-cents]').forEach(option => {
        const qtyInput = option.querySelector('.quantity-input');
        const qty = qtyInput ? parseInt(qtyInput.value || '0', 10) : 0;
        if (qty <= 0) return;

        selectedTotalCents += qty * getUnitPrice(option);

        if (
          purchaseTypeState.type === 'subscription' &&
          parseInt(option.dataset.subscriptionPriceCents || '0', 10) > 0 &&
          option.dataset.sellingPlanId
        ) {
          selectedHasSubscription = true;
        }
      });

      return { selectedTotalCents, selectedHasSubscription };
    }

    function hasSelectedQuantity() {
      return Array.from(buySectionRoot?.querySelectorAll('.quantity-input') || []).some(input => {
        return parseInt(input.value || '0', 10) > 0;
      });
    }

    function renderBuyFreeShippingBar(showBar = hasSelectedQuantity()) {
      if (!freeShippingEnabled) return;
      if (!buyFreeShippingBar || !buyFreeShippingText || !buyFreeShippingProgress || !buyFreeShippingIcon) return;
      if (!Number.isFinite(freeShippingThreshold) || freeShippingThreshold <= 0) {
        buyFreeShippingBar.style.display = 'none';
        return;
      }

      buyFreeShippingBar.style.display = '';
      buyFreeShippingBar.classList.toggle('is-visible', !!showBar);

      const { selectedTotalCents, selectedHasSubscription } = getSelectedPendingTotals();
      const combinedTotalCents = cartBaseTotalCents + selectedTotalCents;
      const hasSubscription = cartHasSubscription || selectedHasSubscription;

      let progressPercent = Math.min((combinedTotalCents / freeShippingThreshold) * 100, 100);
      if (subscriptionFreeDeliveryEnabled && hasSubscription) {
        progressPercent = 100;
      }

      buyFreeShippingProgress.style.width = `${progressPercent}%`;

      if (progressPercent >= 100) {
        buyFreeShippingText.innerHTML = freeShippingReachedTemplate.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        buyFreeShippingIcon.classList.add('free');
      } else {
        const remainingCents = Math.max(freeShippingThreshold - combinedTotalCents, 0);
        const formattedPrice = formatMoneyFromCents(remainingCents);
        const message = freeShippingRemainingTemplate
          .replace('[price]', formattedPrice)
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        buyFreeShippingText.innerHTML = message;
        buyFreeShippingIcon.classList.remove('free');
      }
    }

    async function refreshCartBaseTotals() {
      if (!freeShippingEnabled) return;
      if (!buyFreeShippingBar) return;
      try {
        const response = await fetch('/cart.js', {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;

        const cart = await response.json();
        cartBaseTotalCents = parseInt(cart.total_price || '0', 10);
        cartHasSubscription = Array.isArray(cart.items)
          ? cart.items.some(item => item.selling_plan_allocation)
          : false;
      } catch {
        // Keep current values on request failure.
      } finally {
        renderBuyFreeShippingBar();
      }
    }

    window.addEventListener('cart:refresh', refreshCartBaseTotals);
    document.addEventListener('cart:refresh', refreshCartBaseTotals);
    window.addEventListener('cart:updated', refreshCartBaseTotals);
    document.addEventListener('cart:updated', refreshCartBaseTotals);

    if (window.cartDrawerElement && typeof window.cartDrawerElement.onRefreshFinish === 'function') {
      const originalOnRefreshFinish = window.cartDrawerElement.onRefreshFinish.bind(window.cartDrawerElement);
      window.cartDrawerElement.onRefreshFinish = function (htmlElement) {
        originalOnRefreshFinish(htmlElement);
        refreshCartBaseTotals();
      };
    }
    refreshCartBaseTotals();

    function updateVisibleFlavourPrices() {
      document.querySelectorAll('.flavour-option[data-price-cents]').forEach(option => {
        if (option.classList.contains('is-hidden')) return;

        const priceEl = option.querySelector('[data-flavor-price]');
        if (!priceEl) return;

        const portions = parseInt(option.dataset.portions || '0', 10);
        const base = parseInt(option.dataset.priceCents || '0', 10);
        const sub  = parseInt(option.dataset.subscriptionPriceCents || '0', 10);

        let priceCents = base;
        if (purchaseTypeState.type === 'subscription' && sub > 0) {
          priceCents = sub;
        }

        const formattedTotal = formatMoneyFromCents(priceCents);

        // Update "35 jedál • €XX"
        priceEl.innerHTML = `
          ${portions} ${priceEl.dataset.portionsLabel || ''}
          <span class="separator">&#8226;</span>
          <span>${formattedTotal}</span>
        `;
      });
    }

    function getActivePackageOption() {
      return document.querySelector('.package-product[aria-pressed="true"]');
    }

    function getActiveVersionKey() {
        const activeBtn = document.querySelector('.category-product[aria-pressed="true"]');
        return activeBtn ? activeBtn.dataset.version : null;
    }

    function updatePricePerPortion() {
        const activeVersion = getActiveVersionKey();

        document.querySelectorAll('.package-product[data-version]').forEach(pkg => {
            // jen balíčky aktivní verze
            if (activeVersion && pkg.dataset.version !== activeVersion) return;

            // jen viditelné (ostatní mají display:none)
            if (pkg.style.display === 'none') return;

            const perPortionEl = pkg.querySelector('[data-price-per-portion]');
            if (!perPortionEl) return;

            const portions = parseInt(pkg.dataset.portions || '0', 10);
            const base = parseInt(pkg.dataset.priceCents || '0', 10);
            const sub  = parseInt(pkg.dataset.subscriptionPriceCents || base, 10); // fallback

            let priceCents = base;
            if (purchaseTypeState.type === 'subscription' && sub > 0) {
                priceCents = sub;
            }

            if (portions > 0) {
                const perPortionCents = Math.round(priceCents / portions);
                perPortionEl.textContent = formatMoneyFromCents(perPortionCents);
            }
        });
    }

    function getFirstVisibleFlavourOption() {
      return document.querySelector('.flavour-option:not(.is-hidden)');
    }

    function setVisibleSellingPlanId(planId) {
      if (!planId) return;
      document.querySelectorAll('.flavour-option:not(.is-hidden)').forEach(opt => {
        const subscriptionPrice = parseInt(opt.dataset.subscriptionPriceCents || '0', 10);
        const isOnetimeOnly = opt.dataset.onetimeOnly === 'true';
        if (subscriptionPrice > 0 && !isOnetimeOnly) {
          opt.dataset.sellingPlanId = planId;
        } else {
          opt.dataset.sellingPlanId = '';
        }
      });
    }

    function syncSubscriptionSelect() {
      const select = document.querySelector('[data-subscription-select]');
      const container = document.querySelector('[data-subscription-frequency]');
      if (!select || !container) return;

      const active = getFirstVisibleFlavourOption();
      let plans = [];
      if (active && active.dataset.sellingPlans) {
        try {
          plans = JSON.parse(active.dataset.sellingPlans);
        } catch {
          plans = [];
        }
      }

      if (!plans.length) {
        document.querySelectorAll('.flavour-option:not(.is-hidden)').forEach(opt => {
          opt.dataset.sellingPlanId = '';
        });
        container.style.display = 'none';
        return;
      }

      container.style.display = '';
      select.innerHTML = '';

      const defaultPlan = plans[0];

      const deliveryLabel =
        document.querySelector('.buy-section')?.dataset?.deliveryLabel || 'Delivery';

      plans.forEach((plan) => {
        const opt = document.createElement('option');
        opt.value = plan.id;
        const freq = (plan.frequency || '').trim();
        const freqLower = freq.replace(/^(\p{L})/u, m => m.toLowerCase());
        opt.textContent = `${deliveryLabel} ${freqLower}`;
        if (defaultPlan && plan.id === defaultPlan.id) opt.selected = true;
        select.appendChild(opt);
      });

      setVisibleSellingPlanId(select.value);
    }

    function syncPurchaseTypeUI() {
      const selector = document.querySelector('[data-purchase-type]');
      if (!selector) return;

      const buttons = selector.querySelectorAll('[data-purchase-option]');
      if (!buttons.length) return;

      buttons.forEach(btn => {
        const isActive = btn.dataset.purchaseOption === purchaseTypeState.type;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    function syncSubscriptionDetailsHeight() {
      const details = document.querySelector(
        '[data-purchase-option="subscription"] .subscription-details'
      );
      if (!details) return;
      details.style.setProperty('--subscription-open-h', `${Math.ceil(details.scrollHeight)}px`);
    }

    function getPurchaseTypeLockState() {
      const visibleOptions = Array.from(document.querySelectorAll('.flavour-option:not(.is-hidden)'));
      if (!visibleOptions.length) return { forcedType: null };

      const selectedOptions = visibleOptions.filter(option => {
        const qtyInput = option.querySelector('.quantity-input');
        const qty = qtyInput ? parseInt(qtyInput.value || '0', 10) : 0;
        return qty > 0;
      });

      if (!selectedOptions.length) return { forcedType: null };
      const scope = selectedOptions;
      const hasOnetimeOnly = scope.some(option => option.dataset.onetimeOnly === 'true');
      const hasSubscriptionOnly = scope.some(option => option.dataset.subscriptionOnly === 'true');
      const hasSubscriptionCap = scope.some(option => {
        const sub = parseInt(option.dataset.subscriptionPriceCents || '0', 10);
        return sub > 0 && option.dataset.onetimeOnly !== 'true' && option.dataset.available !== 'false';
      });
      const hasNonSubscriptionCap = scope.some(option => {
        const sub = parseInt(option.dataset.subscriptionPriceCents || '0', 10);
        return !(sub > 0) || option.dataset.onetimeOnly === 'true' || option.dataset.available === 'false';
      });

      if (hasOnetimeOnly && !hasSubscriptionOnly) return { forcedType: 'onetime' };
      if (!hasOnetimeOnly && hasSubscriptionOnly) return { forcedType: 'subscription' };
      // Mixed košík (něco jde na sub, něco ne) držíme vždy jako one-time,
      // jinak by UI dovolilo "Předplatné", ale část položek by stejně šla bez plánu.
      if (hasSubscriptionCap && hasNonSubscriptionCap) return { forcedType: 'onetime' };
      if (!hasSubscriptionCap) return { forcedType: 'onetime' };
      return { forcedType: null };
    }

    function applyPurchaseTypeLock(selector) {
      if (!selector) return;
      const { forcedType } = getPurchaseTypeLockState();
      const buttons = selector.querySelectorAll('[data-purchase-option]');
      let typeChanged = false;

      buttons.forEach(btn => {
        const optionType = btn.dataset.purchaseOption;
        const isDisabled = forcedType ? optionType !== forcedType : false;
        btn.disabled = isDisabled;
        btn.classList.toggle('is-disabled', isDisabled);
        btn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        if (isDisabled) {
          const msg = forcedType === 'onetime' ? onetimeOnlyMessage : subscriptionOnlyMessage;
          btn.dataset.disabledReason = msg;
        } else {
          btn.removeAttribute('data-disabled-reason');
        }
      });

      if (forcedType && purchaseTypeState.type !== forcedType) {
        purchaseTypeState.type = forcedType;
        typeChanged = true;
        if (forcedType === 'subscription') {
          selector.classList.add('has-opened-subscription');
        }
      }
      return typeChanged;
    }

    function updatePurchaseTypeAvailability() {
        const selector = document.querySelector('[data-purchase-type]');
        if (!selector) return;
        const preferSubscriptionByDefault = buySectionRoot?.dataset?.subscriptionFirst === 'true';
        const noSelectedQty = !hasSelectedQuantity();

        const wasHidden =
          selector.style.display === 'none' ||
          window.getComputedStyle(selector).display === 'none';
        const hasSub = hasAnyVisibleSubscription();

        if (!hasSub) {
            selector.style.display = 'none';
            purchaseTypeState.type = 'onetime';

            selector.querySelectorAll('[data-purchase-option]').forEach(btn => {
                const isOnetime = btn.dataset.purchaseOption === 'onetime';
                btn.classList.toggle('is-active', isOnetime);
                btn.setAttribute('aria-pressed', isOnetime ? 'true' : 'false');
            });
        } else {
            selector.style.display = '';
            // Apply the default purchase type only when selector appears from hidden state.
            // Otherwise user cannot manually toggle type while qty = 0 (it would be overwritten on every recalc).
            if (preferSubscriptionByDefault && noSelectedQty && wasHidden) {
              purchaseTypeState.type = hasSub ? 'subscription' : 'onetime';
              if (hasSub) {
                selector.classList.add('has-opened-subscription');
              }
            }
            if (wasHidden) {
              if (window.gsap) {
                gsap.killTweensOf(selector);
                gsap.fromTo(
                  selector,
                  { y: 40, opacity: 0 },
                  {
                    y: 0,
                    opacity: 1,
                    duration: 0.55,
                    ease: 'power2.out',
                    clearProps: 'transform,opacity'
                  }
                );
              } else {
                selector.style.opacity = '1';
                selector.style.transform = 'translateY(0)';
              }
            } else if (selector.style.opacity === '0') {
              selector.style.opacity = '1';
              selector.style.transform = 'translateY(0)';
            }
            const typeChanged = applyPurchaseTypeLock(selector);
            syncPurchaseTypeUI();
            if (typeChanged) {
              updateVisibleFlavourPrices();
              updatePricePerPortion();
            }
        }
    }
// =========================
// TOTAL PRICE CALCULATION
// =========================

function updatePurchaseTypePrices() {
  let totalOnetime = 0;
  let totalSubscription = 0;
  let hasQty = false;

  document.querySelectorAll('.flavour-option[data-price-cents]').forEach(option => {
    if (option.classList.contains('is-hidden')) return;

    const qtyInput = option.querySelector('.quantity-input');
    const qty = qtyInput ? parseInt(qtyInput.value || '0', 10) : 0;

    const base = parseInt(option.dataset.priceCents || '0', 10);
    const sub  = parseInt(option.dataset.subscriptionPriceCents || '0', 10);

    if (qty > 0) {
      hasQty = true;
      totalOnetime += qty * base;
      if (sub > 0) {
        totalSubscription += qty * sub;
      }
    }
  });

  // fallback → cena za 1 ks (stejně jako TOTAL)
  if (!hasQty) {
    const firstVisible = document.querySelector('.flavour-option:not(.is-hidden)');
    if (firstVisible) {
      totalOnetime = parseInt(firstVisible.dataset.priceCents || '0', 10);
      totalSubscription = parseInt(firstVisible.dataset.subscriptionPriceCents || '0', 10);
    }
  }

  // update ceny v UI
  const onetimeEl = document.querySelector('[data-purchase-price="onetime"]');
  const subEl = document.querySelector('[data-purchase-price="subscription"]');

  if (onetimeEl) {
    onetimeEl.textContent = formatMoneyFromCents(totalOnetime);
  }

  if (subEl && totalSubscription > 0) {
    subEl.textContent = formatMoneyFromCents(totalSubscription);
  } else if (subEl) {
    subEl.textContent = '';
  }

  // procentuální rozdíl
  const discountEl = document.querySelector('[data-purchase-discount]');
  if (
    discountEl &&
    totalOnetime > 0 &&
    totalSubscription > 0 &&
    totalSubscription < totalOnetime
  ) {
    const percent = Math.round(
      ((totalOnetime - totalSubscription) / totalOnetime) * 100
    );
    discountEl.textContent = `-${percent} %`;
    discountEl.style.display = '';
  } else if (discountEl) {
    discountEl.style.display = 'none';
  }
}

const buySection = document.querySelector('.buy-section');
const totalEl = document.querySelector('[data-total-price]');
const buyWeightPriceEl = document.querySelector('[data-buy-weight-price]');
const showWeightPrice = buySection?.dataset?.showWeightprice === 'true';

function formatMoneyFromCents(cents) {
  const moneyFormat = buySection?.dataset?.moneyFormat;

  if (window.Shopify && typeof Shopify.formatMoney === 'function' && moneyFormat) {
    return Shopify.formatMoney(cents, moneyFormat);
  }

  if (moneyFormat) {
    const formatWithDelimiters = (amountCents, precision, thousands, decimal) => {
      const precisionNum = Number.isFinite(precision) ? precision : 2;
      const thousandsSep = thousands || ',';
      const decimalSep = decimal || '.';
      const amount = (Number(amountCents || 0) / 100).toFixed(precisionNum);
      const parts = amount.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousandsSep}`);
      const fraction = parts[1] ? `${decimalSep}${parts[1]}` : '';
      return `${whole}${fraction}`;
    };

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    return moneyFormat.replace(placeholderRegex, (_, token) => {
      switch (token) {
        case 'amount':
          return formatWithDelimiters(cents, 2);
        case 'amount_no_decimals':
          return formatWithDelimiters(cents, 0);
        case 'amount_with_comma_separator':
          return formatWithDelimiters(cents, 2, '.', ',');
        case 'amount_with_space_separator':
          return formatWithDelimiters(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(cents, 0, '.', ',');
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(cents, 0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_apostrophe_separator':
          return formatWithDelimiters(cents, 0, "'", '.');
        default:
          return formatWithDelimiters(cents, 2);
      }
    });
  }

  const currency = buySection?.dataset?.currency || 'CZK';
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency
  }).format((cents || 0) / 100);
}

    function recalcTotalPrice() {
        if (!totalEl) return;
        updatePurchaseTypeAvailability();

        let totalCents = 0;
        let totalWeightGrams = 0;
        let totalWeightPriceAmount = 0;
        let totalWeightPriceUnit = '';
        let totalPetDepositCents = 0;
        let hasQty = false;
        let hasMixedWeightUnits = false;

        document.querySelectorAll('.flavour-option[data-price-cents]').forEach(option => {
            if (option.classList.contains('is-hidden')) return;

            const qtyInput = option.querySelector('.quantity-input');
            const qty = qtyInput ? parseInt(qtyInput.value || '0', 10) : 0;
            const weightGrams = parseFloat(option.dataset.weightGrams || '0') || 0;
            const weightPriceAmount = parseFloat(option.dataset.weightPriceAmount || '0') || 0;
            const weightPriceUnit = String(option.dataset.weightPriceUnit || '').trim().toLowerCase();
            const servingsCount = parseInt(option.dataset.servingsCount || option.dataset.portions || '0', 10) || 0;
            const hasPetDeposit = option.dataset.petDepositEligible === 'true';

            if (qty > 0) {
                hasQty = true;
                totalCents += qty * getUnitPrice(option);
                totalWeightGrams += qty * weightGrams;
                if (weightPriceAmount > 0 && weightPriceUnit) {
                  if (!totalWeightPriceUnit) {
                    totalWeightPriceUnit = weightPriceUnit;
                  } else if (totalWeightPriceUnit !== weightPriceUnit) {
                    hasMixedWeightUnits = true;
                  }
                  totalWeightPriceAmount += qty * weightPriceAmount;
                }
                if (petDepositEnabled && hasPetDeposit && bottlePriceCents > 0 && servingsCount > 0) {
                  totalPetDepositCents += qty * servingsCount * bottlePriceCents;
                }
            }
        });

        // když není vybráno nic → cena 1 ks
        if (!hasQty) {
            const firstVisible = document.querySelector('.flavour-option:not(.is-hidden)');
            if (firstVisible) {
                totalCents = getUnitPrice(firstVisible);
                totalWeightGrams = parseFloat(firstVisible.dataset.weightGrams || '0') || 0;
                totalWeightPriceAmount = parseFloat(firstVisible.dataset.weightPriceAmount || '0') || 0;
                totalWeightPriceUnit = String(firstVisible.dataset.weightPriceUnit || '').trim().toLowerCase();
                const servingsCount = parseInt(firstVisible.dataset.servingsCount || firstVisible.dataset.portions || '0', 10) || 0;
                const hasPetDeposit = firstVisible.dataset.petDepositEligible === 'true';
                if (petDepositEnabled && hasPetDeposit && bottlePriceCents > 0 && servingsCount > 0) {
                  totalPetDepositCents = servingsCount * bottlePriceCents;
                }
            }
        }

        totalEl.textContent = formatMoneyFromCents(totalCents);

        if (buyWeightPriceEl) {
          const normalizedWeightUnit = totalWeightPriceUnit === 'l' ? 'l' : 'kg';
          const weightLabel = showWeightPrice && !hasMixedWeightUnits && totalWeightPriceAmount > 0 && totalCents > 0
            ? `${formatMoneyFromCents(Math.round(totalCents / totalWeightPriceAmount))} / ${normalizedWeightUnit}`
            : '';
          const petDepositLabel = totalPetDepositCents > 0
            ? `${petDepositLabelText} ${formatMoneyFromCents(totalPetDepositCents)}`
            : '';
          const metaLabels = [weightLabel, petDepositLabel].filter(Boolean);

          if (metaLabels.length) {
            buyWeightPriceEl.style.display = '';
            buyWeightPriceEl.textContent = metaLabels.join(' • ');
          } else {
            buyWeightPriceEl.style.display = 'none';
            buyWeightPriceEl.textContent = '';
          }
        }

        const totalBox = document.querySelector('.total-price');
        if (totalBox) {
            const getMeasuredTotalPriceHeight = () => {
                const style = window.getComputedStyle(totalBox);
                const gap = parseFloat(style.rowGap || style.gap || '0') || 0;
                const children = Array.from(totalBox.children).filter(child => {
                    return window.getComputedStyle(child).display !== 'none';
                });

                const childrenHeight = children.reduce((sum, child) => {
                    return sum + child.getBoundingClientRect().height;
                }, 0);

                const gapsHeight = children.length > 1 ? gap * (children.length - 1) : 0;
                return Math.ceil(Math.max(totalBox.scrollHeight || 0, childrenHeight + gapsHeight));
            };

            const measureHeight = () => {
                const wasVisible = totalBox.classList.contains('is-visible');
                if (!wasVisible) totalBox.classList.add('is-visible');

                const h = getMeasuredTotalPriceHeight();
                totalBox.style.setProperty('--total-price-h', `${h}px`);

                if (!wasVisible) totalBox.classList.remove('is-visible');
            };

            if (hasQty) {
                requestAnimationFrame(() => {
                    measureHeight();
                    totalBox.classList.add('is-visible');
                });
            } else {
                const h = getMeasuredTotalPriceHeight();
                totalBox.style.setProperty('--total-price-h', `${h}px`);
                requestAnimationFrame(() => totalBox.classList.remove('is-visible'));
            }
        }

        const box = document.querySelector('.addtocart-box');
        if (box) {
            box.classList.toggle('has-total', hasQty);
        }

        updatePurchaseTypePrices();
        renderBuyFreeShippingBar(hasQty);
        renderBuyDebugPanel('recalc');
    }

// initial calculation
recalcTotalPrice();

// update total on +/- click
document.addEventListener('click', (e) => {
  if (e.target.closest('.quantity-button')) {
    setTimeout(recalcTotalPrice, 0);
  }
});

// update total if input value changes
document.addEventListener('input', (e) => {
  if (e.target.classList.contains('quantity-input')) {
    recalcTotalPrice();
  }
});
    /* =========================
       PURCHASE TYPE – UI ONLY
       ========================= */

    const selector = document.querySelector('[data-purchase-type]');
    if (selector) {
        const subscriptionSelect = document.querySelector('[data-subscription-select]');
        if (subscriptionSelect) {
          subscriptionSelect.addEventListener('change', () => {
            setVisibleSellingPlanId(subscriptionSelect.value);
            syncSubscriptionDetailsHeight();
          });
        }
        const buttons = selector.querySelectorAll('[data-purchase-option]');

        buttons.forEach(btn => {
            btn.addEventListener('click', () => {
                if (btn.disabled) return;
                const type = btn.dataset.purchaseOption;
                if (!type) return;

                triggerSelectionHaptic();
                if (type === 'subscription') {
                  syncSubscriptionDetailsHeight();
                  selector.classList.add('has-opened-subscription');
                }

                purchaseTypeState.type = type;

                buttons.forEach(b => {
                    const isActive = b === btn;
                    b.classList.toggle('is-active', isActive);
                    b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
                });

                updateVisibleFlavourPrices();
                recalcTotalPrice();
                updatePricePerPortion();
                updatePurchaseTypePrices();
            });
        });

        updatePurchaseTypeAvailability();
        applyPurchaseTypeLock(selector);
        syncPurchaseTypeUI();
        syncSubscriptionDetailsHeight();
        window.addEventListener('resize', syncSubscriptionDetailsHeight);
        updateVisibleFlavourPrices();
        updatePricePerPortion();
        recalcTotalPrice();
    }
});


/* =========================
   ADD TO CART – MULTI ITEM
   ========================= */

const addToCartButton = document.querySelector('.add-to-cart');
const buySectionEl = document.querySelector('.buy-section');
const isCartOpenDisabled = buySectionEl?.dataset?.disableCart === 'true';
const hasCrossellPopup = !!document.querySelector('#cross-popup');

function getSelectedQuantityTotal() {
  let total = 0;
  document.querySelectorAll('.quantity-input').forEach(input => {
    total += parseInt(input.value || '0', 10);
  });
  return total;
}

function updateAddToCartLabel() {
  if (!addToCartButton) return;

  const totalQty = getSelectedQuantityTotal();
  const labelSelect = addToCartButton.dataset.labelSelect || '';
  const labelAdd = addToCartButton.dataset.labelAdd || '';

  addToCartButton.textContent =
    totalQty === 0 ? labelSelect : labelAdd;
}

function resetPurchaseStateAfterCartAdd() {
  if (!buySectionEl) return;
  purchaseTypeState.type =
    buySectionEl.dataset.subscriptionFirst === 'true' ? 'subscription' : 'onetime';
}

function triggerHapticFeedback() {
  stampHapticsDebugState({
    lastAttempt: 'success',
    lastPath: 'pending',
    lastResult: 'requested',
    lastError: ''
  });

  if (window.__manaHaptics && typeof window.__manaHaptics.trigger === 'function') {
    try {
      window.__manaHaptics.trigger('success');
      stampHapticsDebugState({
        lastPath: 'direct-instance',
        lastResult: 'triggered'
      });
    } catch (error) {
      stampHapticsDebugState({
        lastPath: 'direct-instance',
        lastResult: 'error',
        lastError: error?.message || String(error || 'Unknown trigger error')
      });
    }
    return;
  }

  if (window.__manaHapticsLoading && typeof window.__manaHapticsLoading.then === 'function') {
    stampHapticsDebugState({
      lastPath: 'loading-promise',
      lastResult: 'queued'
    });
    window.__manaHapticsLoading.then((haptics) => {
      try {
        if (haptics && typeof haptics.trigger === 'function') {
          haptics.trigger('success');
          stampHapticsDebugState({
            lastPath: 'loading-promise',
            lastResult: 'triggered'
          });
        } else if (navigator && typeof navigator.vibrate === 'function') {
          navigator.vibrate([25, 55, 35]);
          stampHapticsDebugState({
            lastPath: 'navigator.vibrate-after-promise',
            lastResult: 'fallback-triggered'
          });
        } else {
          stampHapticsDebugState({
            lastPath: 'loading-promise',
            lastResult: 'no-trigger',
            lastError: 'Promise resolved without trigger() and vibrate fallback is unavailable.'
          });
        }
      } catch (error) {
        stampHapticsDebugState({
          lastPath: 'loading-promise',
          lastResult: 'error',
          lastError: error?.message || String(error || 'Unknown trigger error')
        });
      }
    }).catch((error) => {
      stampHapticsDebugState({
        lastPath: 'loading-promise',
        lastResult: 'error',
        lastError: error?.message || String(error || 'Unknown loading promise error')
      });
    });
    return;
  }

  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) {
    stampHapticsDebugState({
      lastPath: 'no-loader-no-fallback',
      lastResult: 'skipped',
      lastError: 'No haptics instance/loading promise and pointer is not coarse.'
    });
    return;
  }
  if (!navigator || typeof navigator.vibrate !== 'function') {
    stampHapticsDebugState({
      lastPath: 'no-loader-no-fallback',
      lastResult: 'skipped',
      lastError: 'navigator.vibrate is unavailable.'
    });
    return;
  }
  navigator.vibrate([25, 55, 35]);
  stampHapticsDebugState({
    lastPath: 'navigator.vibrate',
    lastResult: 'fallback-triggered'
  });
}

function triggerSelectionHaptic() {
  const selectionPattern = [{ duration: 10 }];

  stampHapticsDebugState({
    lastAttempt: 'selection',
    lastPath: 'pending',
    lastResult: 'requested',
    lastError: ''
  });

  if (window.__manaHaptics && typeof window.__manaHaptics.trigger === 'function') {
    try {
      window.__manaHaptics.trigger(selectionPattern, { intensity: 1 });
      stampHapticsDebugState({
        lastPath: 'direct-instance',
        lastResult: 'triggered'
      });
    } catch (error) {
      stampHapticsDebugState({
        lastPath: 'direct-instance',
        lastResult: 'error',
        lastError: error?.message || String(error || 'Unknown trigger error')
      });
    }
    return;
  }

  if (window.__manaHapticsLoading && typeof window.__manaHapticsLoading.then === 'function') {
    stampHapticsDebugState({
      lastPath: 'loading-promise',
      lastResult: 'queued'
    });
    window.__manaHapticsLoading.then((haptics) => {
      try {
        if (haptics && typeof haptics.trigger === 'function') {
          haptics.trigger(selectionPattern, { intensity: 1 });
          stampHapticsDebugState({
            lastPath: 'loading-promise',
            lastResult: 'triggered'
          });
        } else if (navigator && typeof navigator.vibrate === 'function') {
          navigator.vibrate(60);
          stampHapticsDebugState({
            lastPath: 'navigator.vibrate-after-promise',
            lastResult: 'fallback-triggered'
          });
        } else {
          stampHapticsDebugState({
            lastPath: 'loading-promise',
            lastResult: 'no-trigger',
            lastError: 'Promise resolved without trigger() and vibrate fallback is unavailable.'
          });
        }
      } catch (error) {
        stampHapticsDebugState({
          lastPath: 'loading-promise',
          lastResult: 'error',
          lastError: error?.message || String(error || 'Unknown trigger error')
        });
      }
    }).catch((error) => {
      stampHapticsDebugState({
        lastPath: 'loading-promise',
        lastResult: 'error',
        lastError: error?.message || String(error || 'Unknown loading promise error')
      });
    });
    return;
  }

  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) {
    stampHapticsDebugState({
      lastPath: 'no-loader-no-fallback',
      lastResult: 'skipped',
      lastError: 'No haptics instance/loading promise and pointer is not coarse.'
    });
    return;
  }
  if (!navigator || typeof navigator.vibrate !== 'function') {
    stampHapticsDebugState({
      lastPath: 'no-loader-no-fallback',
      lastResult: 'skipped',
      lastError: 'navigator.vibrate is unavailable.'
    });
    return;
  }
  navigator.vibrate(60);
  stampHapticsDebugState({
    lastPath: 'navigator.vibrate',
    lastResult: 'fallback-triggered'
  });
}

function triggerVisualFeedback() {
  if (!addToCartButton) return;
  addToCartButton.classList.remove('feedback-pulse');
  // Force reflow so repeated successful add-to-cart actions replay animation.
  void addToCartButton.offsetWidth;
  addToCartButton.classList.add('feedback-pulse');
}

function scrollElementToViewportCenter(element) {
  if (!element) return;

  const centerToElement = (behavior = 'smooth') => {
    const rect = element.getBoundingClientRect();
    const targetTop =
      window.scrollY + rect.top - (window.innerHeight / 2) + (rect.height / 2);
    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior
    });
  };

  centerToElement('smooth');
}

if (addToCartButton) {
  addToCartButton.addEventListener('click', async () => {
    triggerSelectionHaptic();
    const totalQty = getSelectedQuantityTotal();

    // Buy click -> Scroll to flavours when nothing selected
    if (totalQty === 0) {
      const flavourSection = document.querySelector('.flavour-selector');
      const scrollTarget = flavourSection || buySectionEl;
      if (scrollTarget) {
        scrollElementToViewportCenter(scrollTarget);
      }
      return;
    }

    const items = [];

    document.querySelectorAll('.flavour-option[data-variant-id]').forEach(option => {
      const qtyInput = option.querySelector('.quantity-input');
      if (!qtyInput) return;

      const quantity = parseInt(qtyInput.value, 10);
      const variantId = option.dataset.variantId;
      const sellingPlanId = option.dataset.sellingPlanId;
      const isOnetimeOnly = option.dataset.onetimeOnly === 'true';
      const isSubscriptionOnly = option.dataset.subscriptionOnly === 'true';
      const canUseSubscription = !isOnetimeOnly && !!sellingPlanId;

      if (variantId && quantity > 0) {
        if ((purchaseTypeState.type === 'subscription' && canUseSubscription) || (isSubscriptionOnly && canUseSubscription)) {
          items.push({
            id: variantId,
            quantity,
            selling_plan: sellingPlanId
          });
        } else {
          items.push({
            id: variantId,
            quantity
          });
        }
      }
    });

    if (!items.length) return;

    addToCartButton.disabled = true;
    addToCartButton.classList.add('loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      window.dispatchEvent(new CustomEvent('cart:refresh'));
      if (!hasCrossellPopup && !isCartOpenDisabled && window.cartDrawerElement && typeof window.cartDrawerElement.toggleCart === 'function') {
        window.cartDrawerElement.toggleCart();
      }
      if (hasCrossellPopup) {
        document.dispatchEvent(new Event('product:added'));
      }
      triggerHapticFeedback();
      triggerVisualFeedback();
        // === RESET BUY SECTION AFTER SUCCESSFUL ADD TO CART ===
        document.querySelectorAll('.quantity-input').forEach(input => {
            input.value = 0;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        resetPurchaseStateAfterCartAdd();
        updateAddToCartLabel();
        recalcTotalPrice();
        updateVisibleFlavourPrices();
        updatePricePerPortion();
        updatePurchaseTypePrices();

    } catch (error) {
      gDebug('add to cart failed', error);
    } finally {
      addToCartButton.disabled = false;
      addToCartButton.classList.remove('loading');
    }
  });
}

// Update label when quantity change
document.addEventListener('click', (e) => {
  if (e.target.closest('.quantity-button')) {
    setTimeout(updateAddToCartLabel, 0);
  }
});

updateAddToCartLabel();


/* Animations */
//Parts Arrival
document.addEventListener('DOMContentLoaded', () => {
  if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;
  const isIOSBuySectionViewport =
    (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ) &&
    window.matchMedia?.('(max-width: 768px)').matches;

  if (isIOSBuySectionViewport) return;

  gsap.registerPlugin(ScrollTrigger);

  const boxes = gsap.utils
    .toArray('.parts')
    .filter((box) => !box.matches('.purchase-type-selector'));
  boxes.forEach((box) => {
    gsap.timeline({
      scrollTrigger: {
        trigger: box,
        start: 'top bottom',
        end: '0% 90%',
        scrub: false,
        easing: 'power2.out',
        markers: false
      }
    }).fromTo(
      box,
      { y: 170, opacity: 0 },
      { y: 0, opacity: 1 }
    );
  });

  const gallery = document.querySelector('.product-gallery');
  const galleryShell = document.querySelector('[data-product-gallery-shell]');

  if (gallery) {
    let galleryStartHeight = 0;
    let galleryEndHeight = 0;
    let galleryShrinkDistance = 0;
    let galleryTween = null;
    let galleryRefreshInitHandler = null;
    let viewportResizeTimer = null;
    let galleryViewportWidth = window.innerWidth;

    const isIOSDevice = () =>
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const shouldEnableMobileGallerySticky = () =>
      !!window.matchMedia &&
      window.matchMedia('(max-width: 768px)').matches &&
      !isIOSDevice() &&
      window.innerHeight > 750 &&
      !document.querySelector('.product-gallery-video.is-active:not(.is-hidden)') &&
      !galleryShell?.classList.contains('has-video-pin');

    const recomputeGalleryMetrics = () => {
      const startBase = window.innerHeight * 0.48;
      galleryStartHeight = Math.round(Math.min(192, Math.max(144, startBase)));

      const targetByVh = Math.round(window.innerHeight * 0.15);
      const minTarget = Math.max(80, targetByVh);
      galleryEndHeight = Math.min(galleryStartHeight, minTarget);

      const delta = Math.max(0, galleryStartHeight - galleryEndHeight);
      galleryShrinkDistance = Math.max(60, delta);
    };

    const syncGalleryShellHeight = () => {
      if (!galleryShell) return;
      galleryShell.style.height = `${galleryStartHeight}px`;
    };

    const destroyGalleryStickyAnimation = () => {
      if (galleryTween) {
        if (galleryTween.scrollTrigger) {
          galleryTween.scrollTrigger.kill();
        }
        galleryTween.kill();
        galleryTween = null;
      }
      if (galleryRefreshInitHandler) {
        ScrollTrigger.removeEventListener('refreshInit', galleryRefreshInitHandler);
        galleryRefreshInitHandler = null;
      }

      gallery.classList.remove('is-sticky-compact');
      gallery.style.removeProperty('height');
      if (galleryShell) {
        galleryShell.style.removeProperty('height');
      }
    };

    const createGalleryStickyAnimation = () => {
      if (galleryTween) return;

      recomputeGalleryMetrics();
      syncGalleryShellHeight();

      galleryRefreshInitHandler = () => {
        recomputeGalleryMetrics();
        syncGalleryShellHeight();
      };
      ScrollTrigger.addEventListener('refreshInit', galleryRefreshInitHandler);

      galleryTween = gsap.fromTo(
        gallery,
        {
          height: () => galleryStartHeight
        },
        {
          height: () => galleryEndHeight,
          ease: 'none',
          immediateRender: false,
          scrollTrigger: {
            trigger: gallery,
            start: 'top top+=50',
            end: () => `+=${galleryShrinkDistance}`,
            scrub: 0.15,
            markers: false,
            invalidateOnRefresh: true,
            onRefresh: (self) => {
              gallery.classList.toggle('is-sticky-compact', self.progress > 0.001);
            },
            onEnter: () => gallery.classList.add('is-sticky-compact'),
            onLeaveBack: () => gallery.classList.remove('is-sticky-compact')
          }
        }
      );
    };

    const refreshGalleryStickyMode = () => {
      if (shouldEnableMobileGallerySticky()) {
        createGalleryStickyAnimation();
        recomputeGalleryMetrics();
        syncGalleryShellHeight();
        ScrollTrigger.refresh();
      } else {
        destroyGalleryStickyAnimation();
        ScrollTrigger.refresh();
      }
    };

    refreshGalleryStickyMode();

    window.addEventListener('resize', () => {
      const isMobileViewport = window.matchMedia?.('(max-width: 768px)').matches;
      const nextWidth = window.innerWidth;
      const widthChanged = Math.abs(nextWidth - galleryViewportWidth) > 2;

      if (isMobileViewport && !widthChanged) return;

      galleryViewportWidth = nextWidth;
      if (viewportResizeTimer) clearTimeout(viewportResizeTimer);
      viewportResizeTimer = setTimeout(refreshGalleryStickyMode, 120);
    });

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        galleryViewportWidth = window.innerWidth;
        refreshGalleryStickyMode();
      }, 120);
    });

    window.addEventListener('buy-section:video-gallery-pin', (event) => {
      if (event.detail?.active) {
        destroyGalleryStickyAnimation();
        ScrollTrigger.refresh();
      } else {
        refreshGalleryStickyMode();
      }
    });
  }
});
